import { useState, useEffect, useMemo, useRef, useCallback, type ReactNode } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { customFetch, customerCustomFetch } from "@workspace/api-client-react";
import { useQuery } from "@tanstack/react-query";
import useEmblaCarousel from "embla-carousel-react";
import { CatalogLayout } from "@/components/layouts/CatalogLayout";
import { useBackClose } from "@/hooks/use-back-close";
import { setStoreTimezone, getStoreTimezone } from "@/lib/store-timezone";
import { isPaidPlanId } from "@/lib/plans";
import { useCustomerAuth } from "@/hooks/use-customer-auth";
import { chaveIndicacao, diaDaSemanaNaLoja, fmtPontos, fmtPts, proximoPremio, regraDeGanho, type StoreLoyalty } from "@/lib/loyalty";
import { nextLoyaltyStep, proximaVenda, textoDoHorarioDeVenda, vendaLiberada, normalizeLoyaltyConfig, pointsForOrder, encomendaPorExtenso, faixaDoTurnoTexto, horarioDosTurnos, modoDaEncomenda, turnosDoProduto, NOME_DO_TURNO, type Turno } from "@workspace/pricing";
import { LinkifiedText } from "@/components/ui/linkified-text";
import { FaixaDeBeneficios } from "@/components/catalog/FaixaDeBeneficios";
import { formatCurrency, precoPeso, parsePorcoes, pesoLabel, unidadeMenor } from "@/lib/utils";
import { Search, Info, Plus, Minus, Play, ShoppingBag, X, Sparkles, CalendarDays, CalendarClock, Check, ChevronLeft, ChevronRight, Clock, Pencil, Gift, UtensilsCrossed, Leaf, Loader2, RotateCcw } from "lucide-react";
import { isAvailabilityConfigActive, isDateAllowed, getAvailableTimeSlotsForDate, proximasDatasEncomenda, primeiroMomentoEncomenda, formatAvailabilityDateLabel, summarizeAvailabilityConfig, turnosDoDia, diaServeSoData, type RegraDaEncomenda } from "@/lib/availability";
import type { ProductAvailabilityConfig } from "@workspace/api-client-react";
import { StoreUnavailable } from "@/components/StoreUnavailable";
import { UnitSelector } from "@/components/UnitSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { createCartStore, getCartStore, optionStockCap, type SelectedOption, type CartItem } from "@/hooks/use-cart";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import type { Product, CatalogResponse } from "@workspace/api-client-react/src/generated/api.schemas";
import { aPartirDeComPromocao, getActivePromotion, getBrazilParts, getActiveQuantityPromo, gruposComPromocao, quantityPromoHint, quantityPromoLabel, quantityPromoLineDiscounts, quantityPromoLineHint, quantityPromoUnitDiscountsByLine, usePromotionTick } from "@/lib/promotion";
import { useCatalogRealtime } from "@/hooks/use-catalog-realtime";
import { StoreInactiveCard } from "@/components/StoreInactiveCard";
import { useMesa } from "@/lib/mesa";
import { useToast } from "@/hooks/use-toast";
import { diaEMes, resumoDosItens, tirarPedidoDeNovo, type PedidoDoCliente } from "@/lib/pedir-de-novo";
import type { LinhaDoPedidoAntigo } from "@/hooks/use-cart";
import { EntrarNaMesa, MesaOcupada, QualMesa, VerMinhaConta } from "@/components/catalog/MesaDoCliente";
import { SoPeloQr } from "@/components/catalog/SoPeloQr";
import { ContadorDePreparos } from "@/components/ContadorDePreparos";
import { VideoNoCard, VideoNoCarrossel } from "@/components/catalog/VideoNoCarrossel";
import { imagemPrincipal, midiasDoProduto, videoDoCatalogo, type VideoNoCatalogo } from "@/lib/videoDoProduto";
import { diaOcupado, hojeNaLoja, janelaDaEncomenda, somaDias } from "@/lib/encomendaUnica";
import {
  computeItemTotal,
  flattenGroupSelections,
  formatPreparos,
  groupHasWeights,
  groupsForValidation,
  groupWeightedCount,
  isValidGroupSelection,
  groupMinSelections,
  optionGroupKey,
  optionInPackage,
  optionWeight,
  optionWeightLabel,
  pickAndPriceOptions,
  prepCardResumo,
  prepCount,
  gruposComConta,
  prepCountDaLista,
  prepCountDoItem,
  prepCountFor,
  prepCountLine,
  prepCountOn,
  prepFaltamTexto,
  qtdComNome,
  selectionsByGroupIndex,
  type OptionGroup,
} from "@workspace/pricing";

// «Quer criar uma loja igual a esta?»: o toque passa pela API, que manda para o WhatsApp do próximo número do rodízio
// (Provedores em Rodízio, fase C). O rodízio só anda no toque, e não a cada cardápio aberto.
const LINK_QUERO_UTILIZAR = `${((import.meta.env.VITE_API_URL as string | undefined)?.trim() || import.meta.env.BASE_URL || "/").replace(/\/+$/, "")}/api/whatsapp/falar?texto=${encodeURIComponent("Olá! Quero utilizar o Venda no Zap (cod. 325476)")}`;

// Adicionais (option groups) valem pra qualquer produto que os tenha — não só
// alimentos (ex: combos, marmita precificada por adicional). Já "remover
// ingredientes" continua exclusivo de alimento.
function productHasOptions(product: any): boolean {
  const hasGroups = Array.isArray(product.optionGroups) && product.optionGroups.length > 0;
  const hasRemovable = product.isFood && Array.isArray(product.removableIngredients) && product.removableIngredients.length > 0;
  return hasGroups || hasRemovable;
}

// Produto com preço-base 0 é precificado pelos adicionais ("A partir de X").
function pricesByOptions(product: any): boolean {
  const base = Number(product?.price);
  return !Number.isFinite(base) || base <= 0;
}

// Id anônimo do aparelho pra contar "pessoas" nas visitas ao cardápio. Vive
// no localStorage; sem crypto.randomUUID (navegador velho) cai num id caseiro.
function getVisitorId(): string {
  try {
    const k = "vnz-visitor";
    let v = localStorage.getItem(k);
    if (!v) {
      v = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(k, v);
    }
    return v;
  } catch {
    return "anon";
  }
}

export default function Storefront() {
  const [, params] = useRoute("/loja/:slug");
  const slug = params?.slug || "";
  // Inicializa pela URL pra restaurar a unidade ao voltar do checkout (?unitId=).
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get("unitId"),
  );
  usePromotionTick();

  const cartKey = selectedUnitId ? `${slug}:${selectedUnitId}` : slug;

  // Página aberta reflete mudança do lojista sozinha (sem F5).
  useCatalogRealtime(slug);

  const { data: catalog, isLoading, error } = useQuery<CatalogResponse>({
    queryKey: ["catalog", slug, selectedUnitId],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (selectedUnitId) qs.set("unitId", selectedUnitId);
      // Visita ao cardápio: conta UMA vez por aba (sessionStorage), na primeira
      // busca. Refetch por realtime, foco ou troca de unidade não conta.
      const visitKey = `vnz-visit-${slug}`;
      let counted = true;
      try { counted = sessionStorage.getItem(visitKey) === "1"; } catch { counted = true; }
      if (!counted) {
        qs.set("visit", "1");
        qs.set("visitor", getVisitorId());
        try { sessionStorage.setItem(visitKey, "1"); } catch {}
      }
      const q = qs.toString();
      return customFetch(`/api/catalog/${slug}${q ? `?${q}` : ""}`);
    },
    enabled: !!slug,
  });

  const [search, setSearch] = useState("");
  const [selectedCat, setSelectedCat] = useState("all");
  // Vitrine opt-in: cupons e "Mais vendidos". HOOKS AQUI, antes de qualquer
  // return condicional — abaixo deles o nº de hooks variaria entre renders
  // (foi o crash de 02/09 que derrubou a vitrine no deploy).
  const [cuponsAplicados, setCuponsAplicados] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(`vnz-cupom-${slug}`);
      if (!raw) return [];
      try { const j = JSON.parse(raw); return Array.isArray(j) ? j : [raw]; } catch { return [raw]; }
    } catch { return []; }
  });
  const publicCoupons: any[] = (catalog as any)?.publicCoupons ?? [];
  const bestSellers: string[] = (catalog as any)?.bestSellers ?? [];
  const bestRank = new Map(bestSellers.map((id, i) => [id, i + 1]));
  // Programa de pontos (Ultra): faixa no topo e linha no carrinho. ?ind= no link guarda quem indicou (o checkout
  // manda no 1º pedido). Hooks aqui, antes dos returns condicionais.
  const { customer: clienteLogado, verified: clienteVerificado, isLoading: clienteCarregando, refresh: recarregarCliente } = useCustomerAuth();
  // Pedido na mesa (QR Code, Ultra): ?mesa=&t= conferido no servidor e guardado na aba.
  const mesaQr = useMesa(slug, clienteCarregando ? undefined : clienteLogado?.id ?? null, recarregarCliente);
  const pontosOn = (catalog as any)?.store?.loyaltyEnabled === true;
  const { data: pontos } = useQuery<StoreLoyalty>({
    queryKey: ["store-loyalty", slug, clienteLogado?.id ?? null, clienteVerificado],
    queryFn: () => customerCustomFetch<StoreLoyalty>(`/api/catalog/${slug}/loyalty`),
    enabled: pontosOn && !!slug,
    staleTime: 30_000,
  });
  useEffect(() => {
    const ind = new URLSearchParams(window.location.search).get("ind");
    if (!ind || !slug) return;
    try {
      localStorage.setItem(chaveIndicacao(slug), ind.trim().toUpperCase().slice(0, 12));
    } catch {}
  }, [slug]);
  const usarCupom = (code: string) => {
    // Cumulativo: acumula; senão o último clicado substitui.
    const stackable = (catalog as any)?.store?.stackableCoupons === true;
    const next = stackable
      ? (cuponsAplicados.includes(code) ? cuponsAplicados : [...cuponsAplicados, code])
      : [code];
    try { localStorage.setItem(`vnz-cupom-${slug}`, JSON.stringify(next)); } catch {}
    setCuponsAplicados(next);
  };
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [lightboxProduct, setLightboxProduct] = useState<Product | null>(null);

  // Relógio da loja pros helpers de promoção/data (badge "promo ativa" etc).
  useEffect(() => {
    setStoreTimezone((catalog?.store as { timezone?: string | null } | undefined)?.timezone ?? null);
  }, [catalog?.store]);

  const useCart = getCartStore(cartKey);
  const cartItems = useCart(state => state.items);
  const cartCount = useCart(state => state.getItemCount());
  const cartTotal = useCart(state => state.getTotal());
  const addItem = useCart(state => state.addItem);
  const updateItem = useCart(state => state.updateItem);
  const updateQty = useCart(state => state.updateQuantity);

  const [editingCartItem, setEditingCartItem] = useState<CartItem | null>(null);

  // Carrinho salvo × catálogo que acabou de chegar: preço/promoção viram os de
  // agora e a linha que não fecha mais cai. Some em 8s ou no X — é aviso, não
  // erro: o cliente precisa saber que o carrinho voltou menor do que ele
  // deixou, senão acha que a loja perdeu o pedido dele.
  const syncWithCatalog = useCart((state) => state.syncWithCatalog);
  const [itensCaidos, setItensCaidos] = useState(0);
  useEffect(() => {
    const produtos = catalog?.products;
    if (!produtos || produtos.length === 0) return;
    const caidas = syncWithCatalog(produtos as Product[]);
    if (caidas > 0) setItensCaidos(caidas);
  }, [catalog?.products, syncWithCatalog]);
  useEffect(() => {
    if (itensCaidos === 0) return;
    const t = setTimeout(() => setItensCaidos(0), 8000);
    return () => clearTimeout(t);
  }, [itensCaidos]);

  // O estado "carrinho aberto" vive na URL (?cart=open), não só em estado React.
  // Assim a pilha de histórico fica limpa (loja → carrinho → checkout) e o voltar
  // do navegador in-app — que pode até recarregar a página — reconstrói o estado
  // pela URL, em vez de escapar da SPA pra um 404.
  const cartIsOpenFromUrl = () => new URLSearchParams(window.location.search).get("cart") === "open";
  const [isCartOpen, setIsCartOpen] = useState(cartIsOpenFromUrl);

  // Sincroniza com voltar/avançar (popstate): a URL é a fonte da verdade.
  useEffect(() => {
    const sync = () => setIsCartOpen(cartIsOpenFromUrl());
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);


  const openCart = () => {
    if (cartIsOpenFromUrl()) { setIsCartOpen(true); return; }
    const sp = new URLSearchParams(window.location.search);
    sp.set("cart", "open");
    if (selectedUnitId) sp.set("unitId", selectedUnitId);
    window.history.pushState(null, "", `${window.location.pathname}?${sp.toString()}`);
    setIsCartOpen(true);
  };

  // Fechar pela UI = voltar uma posição no histórico (a entrada ?cart=open),
  // mantendo a pilha consistente; o popstate sync fecha o drawer.
  const closeCart = () => {
    if (cartIsOpenFromUrl()) window.history.back();
    else setIsCartOpen(false);
  };

  // Modal de detalhe do produto: voltar fecha o modal em vez de sair da loja.
  const closeProduct = () => { setSelectedProduct(null); setEditingCartItem(null); };
  useBackClose(!!selectedProduct, closeProduct);

  // «Pedir de novo» (Paiva do Copão, 27/10/2026): as linhas de um pedido antigo entram no carrinho refeitas pelo
  // catálogo de agora — preço de hoje; o que saiu do cardápio, esgotou ou mudou fica de fora, com aviso. O cliente
  // confere no carrinho e finaliza.
  const { toast } = useToast();
  const pedirDeNovoNoCarrinho = useCart((state) => state.pedirDeNovo);
  const pedirDeNovo = useCallback((linhas: LinhaDoPedidoAntigo[]) => {
    const produtos = (catalog?.products ?? []) as Product[];
    const { postas, fora } = pedirDeNovoNoCarrinho(linhas, produtos);
    const nomes = Array.from(new Set(fora)).join(", ");
    if (postas === 0) {
      toast({ title: "Os itens desse pedido não estão disponíveis hoje", description: nomes || undefined, duration: 8000 });
      return;
    }
    toast(
      fora.length > 0
        ? { title: "Alguns itens ficaram de fora", description: `Não estão disponíveis hoje: ${nomes}. O resto está no carrinho, com o preço de hoje.`, duration: 10000 }
        : { title: "Os itens do pedido estão no carrinho", description: "Com o preço de hoje. Confira e finalize.", duration: 6000 },
    );
    openCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog?.products, pedirDeNovoNoCarrinho, toast]);
  // Vindo da Minha conta: o pedido escolhido espera na aba até o catálogo chegar.
  const pedidoDaConta = useRef(false);
  useEffect(() => {
    if (pedidoDaConta.current || !slug || !catalog?.products?.length) return;
    pedidoDaConta.current = true;
    const linhas = tirarPedidoDeNovo(slug);
    if (linhas && linhas.length > 0) pedirDeNovo(linhas);
  }, [slug, catalog?.products, pedirDeNovo]);
  // Cartão «Seu último pedido» no topo: cliente logado que já pediu aqui (53% dos pedidos pelo link, 30 dias até
  // 27/10). Fora da mesa (o QR tem a faixa dele); some depois do clique, para o segundo toque não dobrar o carrinho.
  const [ultimoUsado, setUltimoUsado] = useState(false);
  const { data: ultimosPedidos } = useQuery<PedidoDoCliente[]>({
    queryKey: ["vitrine-ultimo-pedido", slug, clienteLogado?.id ?? null],
    queryFn: () => customerCustomFetch<PedidoDoCliente[]>(`/api/catalog/${slug}/orders?limit=5`),
    enabled: !!slug && !!clienteLogado && !mesaQr.mesa,
    staleTime: 60_000,
    retry: false,
  });
  const ultimoPedido = !mesaQr.mesa && !ultimoUsado && Array.isArray(ultimosPedidos)
    ? ultimosPedidos.find((o) => o.status !== "cancelado" && o.items.length > 0) ?? null
    : null;

  if (isLoading) return <CatalogSkeleton />;
  if (error || !catalog) {
    return (
      <div className="min-h-svh flex items-center justify-center bg-gray-100 p-4">
        <EmptyState
          icon={ShoppingBag}
          title="Loja não encontrada"
          description="O link pode estar quebrado ou a loja saiu do ar. Confirme o endereço com quem te enviou."
        />
      </div>
    );
  }

  const { store, products, categories, isOpen, isBlocked, storeWhatsapp, units, hasFoodProducts } = catalog;
  // Loja com o botão "aberta/fechada" desligado: o servidor recusa o pedido,
  // então o carrinho para AQUI em vez de no último clique do checkout.
  const lojaDesativada = (catalog as { isActive?: boolean }).isActive === false;
  const storeIngredients = ((catalog as any).ingredients ?? []) as Array<{ id: string; name: string; isActive: boolean; stockQuantity: number | null }>;

  if (units && units.length > 1 && !selectedUnitId) {
    return (
      <UnitSelector
        storeName={store.name}
        logoUrl={store.logoUrl}
        units={units}
        colorPrimary={store.colorPrimary}
        colorSecondary={store.colorSecondary}
        colorAccent={store.colorAccent}
        onSelect={(id) => setSelectedUnitId(id)}
      />
    );
  }

  if (isBlocked) {
    return <StoreUnavailable storeName={store.name} logoUrl={store.logoUrl} whatsapp={storeWhatsapp || store.whatsappNumber} colorPrimary={store.colorPrimary} colorSecondary={store.colorSecondary} />;
  }

  // «Link público apenas pela Mesa/Comanda» (Pedro, 31/10/2026, opção B): sem o QR, só o nome da loja e o «Ler QR code».
  // O `soPeloQr` vem calculado do servidor (a chave, o pedido na mesa ligado e o recurso do plano).
  if ((store as { soPeloQr?: boolean }).soPeloQr === true && !mesaQr.mesa) {
    if (mesaQr.conferindo) {
      return (
        <div className="flex min-h-[100svh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }
    return (
      <SoPeloQr
        slug={slug}
        storeName={store.name}
        logoUrl={store.logoUrl}
        colorPrimary={store.colorPrimary}
        porCliente={(store as { tableQrPerClient?: boolean }).tableQrPerClient === true}
        invalida={mesaQr.invalida}
      />
    );
  }

  // Comanda individual (Pedro, 29/10/2026): o cardápio abre depois do login; a comanda que está com alguém já abre
  // bloqueada, antes de pedir nome e WhatsApp; a cadastrada no PDV entra já logada como o cliente (um celular só); na
  // primeira leitura, antes do cardápio, «Em qual mesa você está?».
  if (mesaQr.mesa && mesaQr.porCliente) {
    const ocupada = mesaQr.dono === "ocupada";
    const espera =
      clienteCarregando ||
      mesaQr.dono === "conferindo" ||
      mesaQr.dono === "esperando" ||
      (!!clienteLogado && mesaQr.dono !== "minha" && !ocupada);
    if (!espera && !!clienteLogado && mesaQr.dono === "minha" && !mesaQr.local) {
      return (
        <CatalogLayout store={store} cartItemCount={0} onOpenCart={openCart} isOpen={isOpen}>
          <QualMesa comanda={mesaQr.mesa.mesa} onEnviar={mesaQr.definirLocal} />
        </CatalogLayout>
      );
    }
    if (espera || !clienteLogado || ocupada) {
      return (
        <CatalogLayout store={store} cartItemCount={0} onOpenCart={openCart} isOpen={isOpen}>
          {espera ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : ocupada ? (
            <MesaOcupada />
          ) : (
            <EntrarNaMesa mesa={mesaQr.mesa.mesa} slug={slug} store={store as Record<string, any>} />
          )}
        </CatalogLayout>
      );
    }
  }

  // Produto sem categoria (ou com categoria apagada) cai no grupo "Outros".
  const isUncategorized = (p: any) => !p.categoryId || !categories.find((c: any) => c.id === p.categoryId);

  // Pílula só de categoria com produto VISÍVEL: categoria cujos produtos estão
  // todos desativados aparecia no link e clicar dava "Nenhum produto
  // encontrado" (Pedro, 10/08). Base é `products` (não o filtro de busca) —
  // digitar na busca não pode fazer pílula sumir.
  const visibleCategories = categories.filter((c: any) => products.some((p: any) => p.categoryId === c.id));
  // "Outros" só existe quando há OUTRA categoria visível — loja sem categoria
  // nenhuma segue com a lista corrida, sem pílulas (um "Outros" sozinho seria
  // ruído).
  const showOthers = visibleCategories.length > 0 && products.some(isUncategorized);
  // A barra de pílulas só aparece quando filtra alguma coisa (Pedro, 16/10/2026): sem categoria, o «Todos» sozinho é
  // ruído, e com uma categoria só a pílula dela mostra a mesma lista do «Todos». «Mais vendidos» sempre filtra.
  const mostraPilulas = bestSellers.length > 0 || visibleCategories.length + (showOthers ? 1 : 0) >= 2;
  // Sem a barra, o filtro é sempre «Todos»: se o catálogo (em tempo real) tira a categoria escolhida e a barra some, o
  // cliente não fica preso numa lista vazia sem pílula para voltar.
  const categoriaAtiva = mostraPilulas ? selectedCat : "all";

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchesCat =
      categoriaAtiva === "all" ||
      (categoriaAtiva === "__best__"
        ? bestRank.has(p.id)
        : categoriaAtiva === "__others__" ? isUncategorized(p) : p.categoryId === categoriaAtiva);
    return matchesSearch && matchesCat;
  }).sort((a: any, b: any) => categoriaAtiva === "__best__"
    ? (bestRank.get(a.id) ?? 99) - (bestRank.get(b.id) ?? 99)
    : 0);

  // Sentence case sempre: "pizza"→"Pizza", "PIZZA"→"Pizza" (decisão do Pedro,
  // 10/08 — desistiu de preservar tudo-maiúsculo). Só exibição.
  const displayCategoryName = (name: string) => {
    const t = (name || "").trim();
    if (!t) return t;
    return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
  };

  const getCartItemQty = (productId: string) => {
    return cartItems.filter(i => i.product.id === productId).reduce((sum, i) => sum + i.quantity, 0);
  };

  // Data única no carrinho: produto por encomenda SEM nada a escolher entra direto no carrinho (a data vem no
  // checkout, para o pedido todo) — abrir a tela só para "escolha no fim do pedido" era um toque a mais (Pedro,
  // 05/10/2026, Koma). Com data por produto continua abrindo para escolher a data.
  const pedeData = (p: any) => !!p?.isOrderBased && (store as { orderSingleDate?: boolean }).orderSingleDate !== true;

  const handleQuickAdd = (product: Product) => {
    if (productHasOptions(product) || pedeData(product) || pricesByOptions(product) || (product as any).soldByWeight) {
      setSelectedProduct(product);
    } else {
      addItem(product, 1);
    }
  };

  const handleQuickRemove = (product: Product) => {
    const items = cartItems.filter(i => i.product.id === product.id);
    if (items.length > 0) {
      const last = items[items.length - 1];
      updateQty(last.cartItemId, last.quantity - 1);
    }
  };

  const handleQuickIncrement = (product: Product) => {
    if (productHasOptions(product) || pedeData(product) || pricesByOptions(product)) {
      setSelectedProduct(product);
    } else {
      const items = cartItems.filter(i => i.product.id === product.id);
      if (items.length > 0) {
        const last = items[items.length - 1];
        updateQty(last.cartItemId, last.quantity + 1);
      }
    }
  };

  return (
    <CatalogLayout store={store} cartItemCount={cartCount} onOpenCart={openCart} isOpen={isOpen}>
      {/* Avisos acima da faixa de busca: o bloco dá o respiro dos dois lados —
          com margem só em cima, o aviso colava na faixa (foto do Pedro, 27/09). */}
      {(lojaDesativada || itensCaidos > 0 || mesaQr.mesa || mesaQr.invalida || (ultimoPedido && !lojaDesativada)) && (
        <div className="px-4 py-4 space-y-3" data-testid="storefront-notices">
          {lojaDesativada && <StoreInactiveCard />}
          {ultimoPedido && !lojaDesativada && (
            <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5" data-testid="vitrine-ultimo-pedido">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">Seu último pedido · {diaEMes(ultimoPedido.createdAt)}</p>
                <p className="truncate text-xs text-muted-foreground" data-testid="vitrine-ultimo-pedido-itens">{resumoDosItens(ultimoPedido.items)}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setUltimoUsado(true);
                  pedirDeNovo(ultimoPedido.items);
                }}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
                data-testid="vitrine-pedir-de-novo"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Pedir de novo
              </button>
            </div>
          )}
          {/* «Ver minha conta» no lugar do «Não estou na mesa» (Pedro, 29/10/2026). Com o «1 QR code para cada
              cliente» a faixa sai (o WhatsApp do cliente já está no canto) e o botão fica sozinho no topo. */}
          {mesaQr.mesa && !mesaQr.porCliente && (
            <div className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 text-sm" data-testid="storefront-mesa">
              <UtensilsCrossed className="w-4 h-4 text-primary shrink-0" />
              <span className="flex-1">
                <b>Mesa {mesaQr.mesa.mesa}</b> · o pedido vai direto para a cozinha
              </span>
              <VerMinhaConta slug={slug} mesa={mesaQr.mesa.mesa} token={mesaQr.mesa.token} codeStyle={(store as { orderCodeStyle?: string | null }).orderCodeStyle} pedir={mesaQr.pedirConta} ocupou={mesaQr.ocupou} compacto />
            </div>
          )}
          {mesaQr.mesa && mesaQr.porCliente && (
            <VerMinhaConta slug={slug} mesa={mesaQr.mesa.mesa} token={mesaQr.mesa.token} codeStyle={(store as { orderCodeStyle?: string | null }).orderCodeStyle} porCliente pedir={mesaQr.pedirConta} ocupou={mesaQr.ocupou} />
          )}
          {mesaQr.invalida && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900" data-testid="storefront-mesa-invalida">
              Este QR code não vale mais. Peça ajuda ao atendente.
            </div>
          )}
          {itensCaidos > 0 && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
              <span className="flex-1">
                {itensCaidos === 1
                  ? "Um item saiu do seu carrinho porque mudou no catálogo da loja."
                  : `${itensCaidos} itens saíram do seu carrinho porque mudaram no catálogo da loja.`}
              </span>
              <button
                type="button"
                onClick={() => setItensCaidos(0)}
                aria-label="Fechar aviso"
                className="shrink-0 rounded-md p-0.5 hover:bg-amber-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
      <div className="sticky top-[65px] z-30 bg-card/95 backdrop-blur-md p-4 border-b border-border/50 space-y-4 shadow-sm">
        {(publicCoupons.length > 0 || (pontos?.enabled && pontos.program)) && (
            <FaixaDeBeneficios className="-mx-1" testId="coupon-strip">
              {/* Programa de pontos (Ultra): 1º cartão da fileira (Pedro, 06/10/2026 — antes era uma faixa larga acima,
                  encostada na parte branca). Sozinho na fileira, ocupa a largura toda. */}
              {pontos?.enabled && pontos.program && (
                <Link
                  href={`/conta?store=${slug}`}
                  className={`${publicCoupons.length > 0 ? "flex-none w-[190px]" : "flex-1 min-w-0"} rounded-xl border-[1.5px] border-dashed border-amber-300 bg-amber-50 px-3 py-2`}
                  data-testid="faixa-pontos"
                >
                  <p className="text-[12px] font-extrabold tracking-wide text-amber-700">🎁 PONTOS</p>
                  <p className="text-[11px] font-medium text-foreground/80">
                    {pontos.verified ? `Você tem ${fmtPontos(pontos.balance ?? 0)}` : "Junte e troque por prêmios"}
                  </p>
                  <p className="text-[10px] text-muted-foreground line-clamp-2">{pontos.verified ? "Ver prêmios" : regraDeGanho(pontos.program)}</p>
                </Link>
              )}
              {/* Aviso de frete grátis só de CARONA (exige ≥1 cupom na faixa; o cartão de pontos não conta). */}
              {publicCoupons.length > 0 && Number((store as any)?.freeDeliveryAbove) > 0 && (
                <div className="flex-none w-[190px] rounded-xl border-[1.5px] border-dashed border-green-300 bg-green-50 px-3 py-2">
                  <p className="text-[12px] font-extrabold tracking-wide text-green-700">🚚 ENTREGA GRÁTIS</p>
                  <p className="text-[11px] font-medium text-foreground/80">automática no carrinho</p>
                  <p className="text-[10px] text-muted-foreground">pedidos acima de {formatCurrency(Number((store as any).freeDeliveryAbove))}</p>
                </div>
              )}
              {publicCoupons.map((c: any) => {
                const beneficio = c.discountType === "free_delivery"
                  ? "Frete grátis"
                  : c.discountType === "percent"
                    ? `${String(parseFloat(c.discountValue)).replace(".", ",")}% de desconto`
                    : `${formatCurrency(parseFloat(c.discountValue))} de desconto`;
                const conds: string[] = [];
                if (parseFloat(c.minOrderValue || "0") > 0) conds.push(`acima de ${formatCurrency(parseFloat(c.minOrderValue))}`);
                if (c.firstPurchaseOnly) conds.push("só na 1ª compra");
                if (c.birthdayOnly) conds.push("aniversário");
                const aplicado = cuponsAplicados.includes(c.code);
                return (
                  <button
                    key={c.code}
                    type="button"
                    onClick={(e) => {
                      usarCupom(c.code);
                      // Ticket meio fora da tela rola pro centro — senão o
                      // "✓ aplicado" acende escondido (pedido do Pedro).
                      e.currentTarget.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
                    }}
                    className={`relative flex-none w-[200px] rounded-xl border-[1.5px] border-dashed px-3 py-2 text-left transition-colors ${aplicado ? "border-green-500/70 bg-green-50" : "border-primary/40 bg-primary/5"}`}
                    data-testid={`coupon-${c.code}`}
                  >
                    <p className={`text-[13px] font-extrabold tracking-wide ${aplicado ? "text-green-700" : "text-primary"}`}>{c.code}</p>
                    <p className="text-[11px] font-semibold">{beneficio}</p>
                    <p className="text-[10px] text-muted-foreground">{conds.join(" · ") || " "}</p>
                    <span className={`absolute top-2 right-2 text-[9px] font-extrabold rounded-full px-2 py-0.5 border ${aplicado ? "bg-green-600 border-green-600 text-white" : "border-primary text-primary"}`}>
                      {aplicado ? "✓ aplicado" : "usar"}
                    </span>
                  </button>
                );
              })}
            </FaixaDeBeneficios>
          )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input 
            placeholder={hasFoodProducts ? "Buscar no cardápio..." : "Buscar no catálogo..."}
            className="pl-9 bg-muted/50 border-transparent focus-visible:bg-background h-12 rounded-xl text-base"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        
        {mostraPilulas && (
          <div className="flex gap-2 overflow-x-auto hover-scrollbar" data-testid="vitrine-pilulas">
            {bestSellers.length > 0 && (
              <button
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${selectedCat === '__best__' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                onClick={() => setSelectedCat('__best__')}
              >
                🔥 Mais vendidos
              </button>
            )}
            <button
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${selectedCat === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
              onClick={() => setSelectedCat('all')}
            >
              Todos
            </button>
            {visibleCategories.map(c => (
              <button
                key={c.id}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${selectedCat === c.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                onClick={() => setSelectedCat(c.id)}
              >
                {displayCategoryName(c.name)}
              </button>
            ))}
            {showOthers && (
              <button
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${selectedCat === '__others__' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                onClick={() => setSelectedCat('__others__')}
              >
                Outros
              </button>
            )}
          </div>
        )}
      </div>
      {/* flex-1: a lista ocupa a altura que sobra, para a propaganda do Venda no Zap (mt-auto) descer até o pé. */}
      <div className="flex-1 flex flex-col p-4 pt-5 space-y-4">
        {(() => {
          const hasCategories = categories.length > 0;
          const showGroupHeaders = categoriaAtiva === "all" && hasCategories;
          const grouped: { categoryName: string | null; products: typeof filteredProducts }[] = [];
          if (showGroupHeaders) {
            for (const cat of categories) {
              const catProducts = filteredProducts.filter(p => p.categoryId === cat.id);
              if (catProducts.length > 0) {
                grouped.push({ categoryName: cat.name, products: catProducts });
              }
            }
            const uncategorized = filteredProducts.filter(isUncategorized);
            if (uncategorized.length > 0) {
              // Com outras categorias na tela, o grupo sem categoria ganha
              // título "Outros" (casa com a pílula); sem categorias, segue
              // lista corrida sem cabeçalho.
              grouped.push({ categoryName: showOthers ? "Outros" : null, products: uncategorized });
            }
          } else {
            if (filteredProducts.length > 0) {
              grouped.push({ categoryName: null, products: filteredProducts });
            }
          }
          return (
            <>
              {grouped.length > 0 ? (
                <div className="space-y-6">
                  {grouped.map((group, gi) => (
                    <div key={group.categoryName ?? `__no_cat_${gi}`}>
                      {showGroupHeaders && group.categoryName && (
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 px-1">
                          {group.categoryName}
                        </p>
                      )}
                      <div className="space-y-4">
                        {group.products.map(product => {
                          const qtyInCart = getCartItemQty(product.id);
                          const p = product as any;
                          // Pedro, 26/10/2026: com foto, o botão desce até o fim do card (na altura da linha do preço);
                          // sem foto, a coluna da direita some e o botão vai para a direita da linha do preço, e a
                          // descrição usa a largura toda. Indisponível ou esgotado: sem botão, como antes.
                          // Com vídeo e sem foto, a capa do vídeo faz o papel da foto.
                          const temFoto = !!imagemPrincipal(product.imageUrl, videoDoCatalogo(p.video));
                          const foraDoHorario = foraDoHorarioDeVenda(product);
                          const vende = product.status !== 'indisponivel' && (product.stock === null || product.stock > 0) && !foraDoHorario;
                          const botao = (naColuna: boolean) => qtyInCart > 0 ? (
                            <div className={`flex items-center bg-primary/10 border border-primary/20 rounded-full justify-center ${naColuna ? "w-full" : "shrink-0"}`} data-testid={`card-qtd-${product.id}`}>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleQuickRemove(product); }}
                                className="w-8 h-8 flex items-center justify-center text-primary hover:bg-primary/20 rounded-full transition-colors"
                              >
                                <Minus className="w-3.5 h-3.5" />
                              </button>
                              <span className="w-7 text-center text-sm font-bold text-primary">{qtyInCart}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleQuickIncrement(product); }}
                                disabled={(product.stock !== null && qtyInCart >= product.stock) || ((product as any).maxPerCart != null && qtyInCart >= (product as any).maxPerCart)}
                                className="w-8 h-8 flex items-center justify-center text-primary hover:bg-primary/20 rounded-full transition-colors disabled:opacity-40"
                              >
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleQuickAdd(product); }}
                              className={`flex items-center gap-0.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-colors shadow-sm justify-center text-[12px] ${naColuna ? "w-full" : "shrink-0"}`}
                              data-testid={`card-adicionar-${product.id}`}
                            >
                              <Plus className="w-4 h-4" />
                              Adicionar
                            </button>
                          );
                          return (
                            <div 
                              key={product.id} 
                              // bg-card + shadow-sm = mesma receita do checkout. Era
                              // `bg-background`, a MESMA cor da página (1,00:1): o card só
                              // existia por causa da borda a 50%.
                              className={`bg-card border border-border/50 shadow rounded-2xl p-4 flex gap-4 transition-all ${(product.stock !== null && product.stock === 0) || product.status === 'indisponivel' || foraDoHorario ? 'opacity-60 grayscale-[0.3]' : ''}`}
                            >
                              <div className="flex-1 min-w-0 flex flex-col justify-between">
                                <div className="cursor-pointer" onClick={() => vende && setSelectedProduct(product)}>
                                  <h3 className="font-bold text-foreground text-lg leading-tight">
                                    {categoriaAtiva === "__best__" && bestRank.has(product.id) && (
                                      <span className="inline-block align-middle mr-1.5 text-[10px] font-bold text-white bg-orange-600 px-1.5 py-0.5 rounded-full leading-none">nº {bestRank.get(product.id)}</span>
                                    )}
                                    {product.name}
                                  </h3>
                                  {product.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2 leading-relaxed ml-[1px]"><LinkifiedText text={product.description} /></p>}
                                  {prepCountOn((product as any).prepCount) && (() => {
                                    // Pacote de escolhas: o cartão já diz quantos entram e quanto custa o extra.
                                    const pc = prepCount((product as any).prepCount, 0);
                                    const r = prepCardResumo(pc, formatCurrency);
                                    return (
                                      <p className="text-xs text-muted-foreground mt-1 ml-[1px]" data-testid="prep-card-line">
                                        <b className="font-semibold text-foreground">{r.pacote}</b>
                                        {r.extras && <> · {r.extras}</>}
                                      </p>
                                    );
                                  })()}
                                </div>
                                <div className="mt-3 flex items-center gap-2 flex-wrap ml-[1px]">
                                  {(() => {
                                    if (pricesByOptions(product)) {
                                      // Com a promoção em % nas opções (Pedro, 23/10/2026), o valor com desconto e o
                                      // original riscado, como a promoção dos outros produtos.
                                      const ap = aPartirDeComPromocao(product, "link");
                                      if (ap) {
                                        // "A partir de" na linha de cima: o preço começa na mesma
                                        // coluna dos outros cards (pedido do Pedro, 01/10/2026).
                                        return (
                                          <span className="flex flex-col">
                                            <span className="text-xs font-medium leading-tight text-muted-foreground">A partir de</span>
                                            {ap.original != null ? (
                                              <span className="flex items-baseline gap-2 flex-wrap" data-testid={`a-partir-promo-${product.id}`}>
                                                <span className="font-bold text-accent text-lg">{formatCurrency(ap.valor)}</span>
                                                <span className="text-sm leading-tight text-muted-foreground line-through">{formatCurrency(ap.original)}</span>
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-accent-foreground bg-accent px-2 py-0.5 rounded-full">{store?.promotionBadgeText || "Promo"}</span>
                                              </span>
                                            ) : (
                                              <span className="font-bold text-primary text-lg">{formatCurrency(ap.valor)}</span>
                                            )}
                                          </span>
                                        );
                                      }
                                    }
                                    const promo = getActivePromotion(product, new Date(), "link");
                                    if (promo.active) {
                                      // Preço riscado numa linha só dele (basis-full), em cima: o
                                      // preço final começa na mesma coluna dos outros cards, como o
                                      // "A partir de" (pedido do Pedro, 03/10/2026).
                                      return (
                                        <>
                                          <span className="basis-full -mb-1.5 text-sm leading-tight text-muted-foreground line-through">{formatCurrency(promo.originalPrice)}</span>
                                          <span className="font-bold text-accent text-lg">{formatCurrency(promo.finalPrice)}</span>
                                          <span className="text-[10px] font-bold uppercase tracking-wider text-accent-foreground bg-accent px-2 py-0.5 rounded-full">{store?.promotionBadgeText || "Promo"}</span>
                                          {promo.discountPercent > 0 && (
                                            <span className="text-[10px] font-bold text-accent bg-accent/10 border border-accent/30 px-2 py-0.5 rounded-full">−{promo.discountPercent}%</span>
                                          )}
                                        </>
                                      );
                                    }
                                    return <span className="font-bold text-primary text-lg">{(product as any).soldByWeight ? precoPeso(Number(product.price), (product as any).weightUnit).valor : formatCurrency(product.price)}{(product as any).soldByWeight ? <span className="text-xs font-semibold text-muted-foreground">{precoPeso(Number(product.price), (product as any).weightUnit).sufixo}</span> : null}</span>;
                                  })()}
                                  {(() => {
                                    // Selo da promo POR QUANTIDADE (o preço unitário não muda,
                                    // então o caminho do line-through acima não a mostra).
                                    const qtyLabel = quantityPromoLabel(getActiveQuantityPromo(product, new Date(), "link"));
                                    return qtyLabel
                                      ? <span className="text-[10px] font-bold uppercase tracking-wider text-accent-foreground bg-accent px-2 py-0.5 rounded-full">{qtyLabel}</span>
                                      : null;
                                  })()}
                                  {p.isOrderBased && <span className="text-xs font-semibold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full flex items-center gap-1"><CalendarDays className="w-3 h-3" />Sob encomenda</span>}
                                  {p.isOrderBased && (isAvailabilityConfigActive(p.availabilityConfig) || p.orderUntilDate) && (() => {
                                    // «Aceitar encomendas até» (26/10/2026): depois dele, «Sem datas próximas». Sem a disponibilidade
                                    // limitada, o selo só aparece então (o «Disp.» continua sendo de quem limita os dias).
                                    const next = isAvailabilityConfigActive(p.availabilityConfig)
                                      ? proximasDatasEncomenda(p.availabilityConfig as ProductAvailabilityConfig, p.minAdvanceDays ?? 0, p.minAdvanceHours, 1, new Date(), 60, undefined, limiteDaEncomenda(p), inicioDaEncomenda(p))
                                      : encomendaPassouDoLimite(p) ? [] : null;
                                    if (next === null) return null;
                                    if (next.length === 0) return <span className="text-xs font-semibold text-accent bg-accent/10 border border-accent/20 px-2 py-0.5 rounded-full flex items-center gap-1"><CalendarClock className="w-3 h-3" />Sem datas próximas</span>;
                                    return <span className="text-xs font-semibold text-accent bg-accent/10 border border-accent/20 px-2 py-0.5 rounded-full flex items-center gap-1"><CalendarClock className="w-3 h-3" />Disp. {formatAvailabilityDateLabel(next[0])}</span>;
                                  })()}
                                  {p.servings && <span className="text-xs text-secondary bg-secondary/15 px-2 py-0.5 rounded-full font-medium">Serve {p.servings} {p.servings === 1 ? 'pessoa' : 'pessoas'}</span>}
                                  {p.slices > 0 && <span className="text-xs text-secondary bg-secondary/15 px-2 py-0.5 rounded-full font-medium">{p.slices} {p.slices === 1 ? 'fatia' : 'fatias'}</span>}
                                  {p.isVegan && <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full flex items-center gap-1"><Leaf className="w-3 h-3" />Vegano</span>}
                                  {(p as any).maxPerCart && <span className="text-xs font-semibold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full flex items-center gap-1"><ShoppingBag className="w-3 h-3" />Máx. {(p as any).maxPerCart} por entrega</span>}
                                  {product.status === 'indisponivel' ? <span className="text-xs font-bold text-destructive bg-destructive/10 px-2 py-1 rounded">Indisponível</span> : product.stock !== null && product.stock === 0 && <span className="text-xs font-bold text-destructive bg-destructive/10 px-2 py-1 rounded">Esgotado</span>}
                                  {foraDoHorario && product.status !== 'indisponivel' && !(product.stock !== null && product.stock === 0) && (
                                    <span className="text-xs font-bold text-destructive bg-destructive/10 px-2 py-1 rounded flex items-center gap-1" data-testid={`card-fora-do-horario-${product.id}`}>
                                      <Clock className="w-3 h-3" />
                                      {foraDoHorario}
                                    </span>
                                  )}
                                  {!temFoto && vende && <div className="ml-auto">{botao(false)}</div>}
                                </div>
                                {(() => {
                                  const promo = getActivePromotion(product, new Date(), "link");
                                  if (promo.active && promo.endsLabel) {
                                    return <p className="text-[11px] text-accent/80 font-medium mt-1 ml-[1px]">⏱ {promo.endsLabel}</p>;
                                  }
                                  return null;
                                })()}
                              </div>
                              {temFoto && (
                              <div className="flex flex-col items-center justify-between gap-2" style={{ width: '28%' }}>
                                {(() => {
                                  const galleryUrls = Array.isArray((p as any).imageUrls)
                                    ? ((p as any).imageUrls as any[]).filter((u): u is string => typeof u === "string" && u.length > 0)
                                    : [];
                                  // Vídeo do produto (Profissional e Ultra): selo «▶ vídeo», e o vídeo tocando quando ele é o primeiro.
                                  const video = videoDoCatalogo(p.video);
                                  const principal = imagemPrincipal(product.imageUrl, video);
                                  if (!principal) return null;
                                  const totalPhotos = (galleryUrls.length > 0 ? galleryUrls.length : (product.imageUrl ? 1 : 0)) + (video ? 1 : 0);
                                  return (
                                    <div
                                      className="relative w-full aspect-square rounded-xl overflow-hidden bg-muted shadow-sm cursor-pointer"
                                      onClick={() => setLightboxProduct(product)}
                                    >
                                      {video && principal === video.capa
                                        ? <VideoNoCard video={video} alt={product.name} />
                                        : <img src={principal} alt={product.name} className="w-full h-full object-cover" loading="lazy" />}
                                      {video && (
                                        <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-white bg-black/55 backdrop-blur-sm px-1.5 py-0.5 rounded-full leading-none" data-testid={`card-video-${product.id}`}>
                                          <Play className="w-2.5 h-2.5 fill-current" />
                                          vídeo
                                        </span>
                                      )}
                                      {totalPhotos > 1 && (
                                        <span className="absolute bottom-1.5 right-1.5 text-[10px] font-semibold text-white bg-black/55 backdrop-blur-sm px-1.5 py-0.5 rounded-full leading-none">
                                          1/{totalPhotos}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                                {vende && botao(true)}
                              </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={Search}
                  title="Nenhum produto encontrado"
                  description={search ? `Não achamos nada com "${search}". Tente outras palavras ou limpe a busca.` : "Nenhum produto disponível no momento."}
                  action={search ? (
                    <Button variant="outline" onClick={() => setSearch("")}>
                      <X className="w-4 h-4 mr-1" /> Limpar busca
                    </Button>
                  ) : undefined}
                />
              )}
              {grouped.length > 0 && store && !isPaidPlanId(store.plan) && (
                // Propaganda do Venda no Zap nas lojas grátis como o último "produto" da lista (Pedro, 08/10/2026):
                // aparece no fim da rolagem, junto dos produtos; com pouco produto, desce até o pé da tela (mt-auto).
                // O cartão inteiro abre o WhatsApp.
                <a
                  href={LINK_QUERO_UTILIZAR}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block mt-auto bg-card border border-border/50 shadow rounded-2xl p-4 text-center text-sm text-muted-foreground"
                  data-testid="rodape-venda-no-zap"
                >
                  Quer criar uma loja igual a esta?
                  <span className="block mt-1 font-semibold text-primary group-hover:underline">Toque aqui</span>
                </a>
              )}
            </>
          );
        })()}
      </div>
      {cartCount > 0 && !isCartOpen && (
        // Barra na largura da coluna de produtos (max-w-3xl, igual ao container
        // da página) — o pill solto no canto sumia no desktop.
        <div className="fixed bottom-4 inset-x-4 z-40 flex justify-center pointer-events-none">
          <button
            onClick={openCart}
            className="cart-nudge pointer-events-auto w-full max-w-3xl bg-primary text-primary-foreground rounded-2xl px-5 py-3.5 flex items-center justify-between gap-2 shadow-xl shadow-primary/30 hover:shadow-2xl hover:shadow-primary/40 transition-all hover:-translate-y-0.5 active:scale-[0.99]"
          >
            <span className="flex items-center gap-2">
              <span className="w-7 h-7 bg-primary-foreground/20 rounded-full flex items-center justify-center">
                <ShoppingBag className="w-3.5 h-3.5" />
              </span>
              <span className="font-bold text-sm">Ver Carrinho</span>
            </span>
            <span className="font-bold text-sm">{formatCurrency(cartTotal)}</span>
          </button>
        </div>
      )}
      <ProductDetailModal
        store={store}
        storeIngredients={storeIngredients}
        product={selectedProduct}
        slug={slug}
        selectedUnitId={selectedUnitId}
        editingItem={editingCartItem}
        maxPerCart={(selectedProduct as any)?.maxPerCart ?? null}
        onClose={closeProduct}
        onAdd={(qty, notes, selectedOptions, removedIngredients, scheduledDate, scheduledTime, weightKg, scheduledShift) => {
          if (editingCartItem) {
            updateItem(editingCartItem.cartItemId, qty, notes, selectedOptions, removedIngredients, scheduledDate, scheduledTime, weightKg, scheduledShift);
          } else if (selectedProduct) {
            addItem(selectedProduct, qty, notes, selectedOptions, removedIngredients, scheduledDate, scheduledTime, weightKg, scheduledShift);
          }
          setSelectedProduct(null);
          setEditingCartItem(null);
        }}
      />
      <ProductLightbox
        product={lightboxProduct}
        onClose={() => setLightboxProduct(null)}
      />
      <CartDrawer 
        isOpen={isCartOpen} 
        onClose={closeCart}
        slug={slug} 
        store={store}
        cartState={useCart}
        catalogIsOpen={isOpen}
        lojaDesativada={lojaDesativada}
        selectedUnitId={selectedUnitId}
        products={products}
        hasFoodProducts={hasFoodProducts}
        onOpenProduct={setSelectedProduct}
        onEditItem={(item) => { setEditingCartItem(item); setSelectedProduct(item.product); }}
        pontos={pontos}
        mesa={mesaQr.mesa?.mesa ?? null}
      />
      {cartCount > 0 && !isCartOpen && <div className="h-4" />}
    </CatalogLayout>
  );
}

// `sobre`: camada por cima da foto e por baixo das setas, dos pontos e do X (o contador do pacote).
function ProductImageCarousel({ images, video = null, alt, onClose, fit = "cover", containerClassName, showCloseButton = true, sobre }: { images: string[]; video?: VideoNoCatalogo | null; alt: string; onClose: () => void; fit?: "cover" | "contain"; containerClassName?: string; showCloseButton?: boolean; sobre?: ReactNode }) {
  const imgFitClass = fit === "contain" ? "object-contain" : "object-cover";
  // O vídeo entra entre as fotos no lugar que o lojista deu.
  const midias = useMemo(() => midiasDoProduto(images, video), [images, video]);
  // Os pontinhos saem enquanto o vídeo toca: ficavam por cima da barra do vídeo.
  const [videoTocando, setVideoTocando] = useState(false);
  const containerCls = containerClassName ?? "w-full h-64 bg-muted relative shrink-0";
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "start" });
  const [selectedIdx, setSelectedIdx] = useState(0);

  const scrollTo = useCallback((i: number) => { emblaApi?.scrollTo(i); }, [emblaApi]);
  const scrollPrev = useCallback(() => { emblaApi?.scrollPrev(); }, [emblaApi]);
  const scrollNext = useCallback(() => { emblaApi?.scrollNext(); }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSel = () => setSelectedIdx(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSel);
    onSel();
    return () => { emblaApi.off("select", onSel); };
  }, [emblaApi]);

  useEffect(() => {
    if (emblaApi) emblaApi.reInit();
  }, [emblaApi, midias.length]);

  if (midias.length === 1) {
    const unica = midias[0]!;
    return (
      <div className={containerCls}>
        {unica.tipo === "video"
          ? <VideoNoCarrossel video={unica.video} ativo alt={alt} />
          : <img src={unica.url} alt={alt} className={`w-full h-full ${imgFitClass}`} />}
        {sobre}
        {showCloseButton && (
          <Button variant="secondary" size="icon" className="absolute top-4 right-4 rounded-full bg-background/80 backdrop-blur" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={containerCls}>
      <div ref={emblaRef} className="overflow-hidden h-full">
        <div className="flex h-full">
          {midias.map((m, i) => (
            <div key={m.tipo === "foto" ? `${m.url}-${i}` : `video-${i}`} className="flex-[0_0_100%] min-w-0 h-full flex items-center justify-center">
              {m.tipo === "video"
                ? <VideoNoCarrossel video={m.video} ativo={selectedIdx === i} alt={alt} onTocando={setVideoTocando} />
                : <img src={m.url} alt={`${alt} - foto ${i + 1}`} className={`w-full h-full ${imgFitClass}`} />}
            </div>
          ))}
        </div>
      </div>
      {sobre}

      <button
        type="button"
        onClick={scrollPrev}
        aria-label="Foto anterior"
        disabled={selectedIdx === 0}
        className="flex absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background/85 backdrop-blur items-center justify-center hover:bg-background shadow disabled:opacity-30 disabled:cursor-not-allowed transition-opacity z-10"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={scrollNext}
        aria-label="Próxima foto"
        disabled={selectedIdx === midias.length - 1}
        // Com a camada por cima (`sobre`), a seta vai para o canto, como a outra: recuada, ela cobria o cartão.
        className={`flex absolute top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background/85 backdrop-blur items-center justify-center hover:bg-background shadow disabled:opacity-30 disabled:cursor-not-allowed transition-opacity z-10 ${showCloseButton && !sobre ? "right-16" : "right-2"}`}
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      <div className={`absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/35 backdrop-blur-sm ${videoTocando ? "hidden" : ""}`}>
        {midias.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => scrollTo(i)}
            aria-label={`Ir para foto ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-200 ${selectedIdx === i ? "w-5 bg-white" : "w-1.5 bg-white/55 hover:bg-white/80"}`}
          />
        ))}
      </div>

      {showCloseButton && (
        <Button variant="secondary" size="icon" className="absolute top-4 right-4 rounded-full bg-background/80 backdrop-blur z-10" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}

function ProductLightbox({ product, onClose }: { product: Product | null; onClose: () => void }) {
  const images = useMemo(() => {
    if (!product) return [] as string[];
    const gallery = Array.isArray((product as any).imageUrls)
      ? ((product as any).imageUrls as any[]).filter((u): u is string => typeof u === "string" && u.length > 0)
      : [];
    if (gallery.length > 0) return gallery;
    return product.imageUrl ? [product.imageUrl] : [];
  }, [product]);
  const video = useMemo(() => videoDoCatalogo((product as any)?.video), [product]);

  const open = !!product && (images.length > 0 || !!video);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-[480px] p-0 overflow-hidden gap-0 rounded-2xl bg-black border-none max-h-[90vh] flex items-center justify-center">
        {open && (
          <ProductImageCarousel
            images={images}
            video={video}
            alt={product!.name}
            onClose={onClose}
            fit="contain"
            containerClassName="w-full h-[80vh] bg-black relative shrink-0"
            showCloseButton={false}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// Horário de venda (T-0060, Pedro, 04/11/2026): fora da faixa, no relógio da loja, o produto que não é Por encomenda fica
// indisponível, com o próximo horário («Disponível terça, das 16h às 23h59»); null = pode pedir agora. A página se
// redesenha de 30 em 30 s (usePromotionTick), então o produto volta sozinho quando a faixa abre.
function foraDoHorarioDeVenda(p: any, agora: Date = new Date()): string | null {
  if (!p || p.isOrderBased || !p.saleHours) return null;
  const r = getBrazilParts(agora);
  const min = r.hours * 60 + r.minutes;
  if (vendaLiberada(p.saleHours, r.weekday, min)) return null;
  const prox = proximaVenda(p.saleHours, r.weekday, min);
  return prox ? `Disponível ${textoDoHorarioDeVenda(prox)}` : "Indisponível agora";
}

function ProductDetailModal({ store, storeIngredients, product, slug, selectedUnitId, editingItem, maxPerCart, onClose, onAdd }: { store: CatalogResponse["store"], storeIngredients: Array<{ id: string; name: string; isActive: boolean; stockQuantity: number | null }>, product: Product|null, slug: string, selectedUnitId: string|null, editingItem?: CartItem|null, maxPerCart?: number|null, onClose: ()=>void, onAdd: (qty:number, notes:string, selectedOptions?: SelectedOption[], removedIngredients?: string[], scheduledDate?: string, scheduledTime?: string, weightKg?: number, scheduledShift?: string)=>void }) {
  const ingredientById = new Map(storeIngredients.map((i) => [i.id, i]));
  // Ingrediente deletado: a opção some por completo (filtrada no render).
  // Inativo / sem estoque: continua visível como "Esgotado" desabilitado — ou some, com
  // "Esconder opções esgotadas do link público" ligado (ver opcoesDoGrupo).
  const optionIsOrphaned = (opt: any): boolean =>
    !!opt?.ingredientId && !ingredientById.has(opt.ingredientId);
  const isOptionUnavailable = (opt: any): boolean => {
    // Personalizada: estoque no próprio item (null/ausente = sem controle).
    if (!opt?.ingredientId) return opt?.stock != null && opt.stock <= 0;
    const ing = ingredientById.get(opt.ingredientId);
    if (!ing) return false; // órfão é filtrado antes; aqui é defensivo
    if (!ing.isActive) return true;
    if (ing.stockQuantity != null && ing.stockQuantity <= 0) return true;
    return false;
  };
  // Opções que o grupo mostra. Com "Esconder opções esgotadas" (Pedro, 06/10/2026) a esgotada/pausada some, menos
  // a que já está escolhida (item do carrinho em edição). Se o que sobra não fecha o mínimo do grupo, mostra tudo
  // como antes, com o "Esgotado": o cliente entende por que não consegue pedir. Grupo opcional sem nenhuma some.
  const esconderEsgotadas = (store as any).hideUnavailableOptions === true;
  const opcoesDoGrupo = (group: any, sel: string[]): any[] => {
    const todas = (group?.options || []).filter((opt: any) => !optionIsOrphaned(opt));
    if (!esconderEsgotadas) return todas;
    const disponiveis = todas.filter((opt: any) => !isOptionUnavailable(opt) || sel.includes(opt.name));
    return disponiveis.length < groupMinSelections(group as OptionGroup) ? todas : disponiveis;
  };
  // Nome atual do ingrediente (sobrescreve o snapshot pra refletir rename
  // imediato no storefront). Fallback pro snapshot se algo der errado.
  const displayName = (opt: any): string => {
    if (opt?.ingredientId) {
      const ing = ingredientById.get(opt.ingredientId);
      if (ing) return ing.name;
    }
    return opt?.name ?? "";
  };
  // "Restam X" pra estoque baixo (≤10). > 10 não mostra pra não poluir.
  const LOW_STOCK_THRESHOLD = 10;
  const lowStockLabel = (opt: any): string | null => {
    // Configurações → "Mostrar quantidade restante dos adicionais no link".
    if ((store as any).showOptionStock === false) return null;
    if (!opt?.ingredientId) {
      const s = opt?.stock;
      if (s == null || s <= 0 || s > LOW_STOCK_THRESHOLD) return null;
      return s === 1 ? "Resta 1" : `Restam ${s}`;
    }
    const ing = ingredientById.get(opt.ingredientId);
    if (!ing || !ing.isActive) return null;
    if (ing.stockQuantity == null || ing.stockQuantity <= 0) return null;
    if (ing.stockQuantity > LOW_STOCK_THRESHOLD) return null;
    return ing.stockQuantity === 1 ? "Resta 1" : `Restam ${ing.stockQuantity}`;
  };
  // Limite de quantas vezes a opção pode aparecer no item (multi-select).
  // Cap = estoque do ingrediente, se rastreado. Cross-cart e quantity do item
  // ainda podem estourar — o server bloqueia via reserveIngredientStock.
  const stockCap = (opt: any): number | null => {
    if (!opt?.ingredientId) return opt?.stock ?? null;
    const ing = ingredientById.get(opt.ingredientId);
    if (!ing || !ing.isActive) return null;
    return ing.stockQuantity ?? null;
  };
  const [qty, setQty] = useState(1);
  // Porção do produto por peso (kg). Default: a menor — sempre há escolha
  // válida, o cliente só ajusta. Edição preserva a porção da linha.
  const porcoes = (product as any)?.soldByWeight ? parsePorcoes((product as any)?.portions) : [];
  const pesoLivre = !!(product as any)?.soldByWeight && porcoes.length === 0;
  // g e ml digitam e mostram em unidades (1 ml = 1 g); ml é perfume e afins: «Quanto você quer?» (T-0056).
  const unidadeDoPeso = (product as any)?.weightUnit as string | undefined;
  const menor = unidadeMenor(unidadeDoPeso);
  const emGramas = !!menor;
  const [porcaoKg, setPorcaoKg] = useState<number | null>((editingItem as any)?.weightKg ?? (porcoes.length > 0 ? porcoes[0]!.kg : null));
  const [pesoStr, setPesoStr] = useState<string>((editingItem as any)?.weightKg != null ? String((editingItem as any).weightKg).replace(".", ",") : "");
  const [pesoErro, setPesoErro] = useState(false);
  const [notes, setNotes] = useState("");
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const [removed, setRemoved] = useState<string[]>([]);
  const [scheduledDate, setScheduledDate] = useState<string | null>(null);
  const [scheduledTime, setScheduledTime] = useState<string | null>(null);
  const [scheduledShift, setScheduledShift] = useState<Turno | null>(null);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(() => new Date().getFullYear());

  const p = product as any;
  const isOrderBased = p?.isOrderBased;
  // Data única no carrinho: o produto por encomenda não pede data aqui (escolhe no checkout, para o pedido todo).
  const dataUnica = (store as { orderSingleDate?: boolean } | undefined)?.orderSingleDate === true;
  // O que o produto pede (12/10/2026): horário (padrão), turno ou só a data.
  const regra: RegraDaEncomenda = {
    modo: modoDaEncomenda(p),
    turnos: turnosDoProduto(p),
    horarios: horarioDosTurnos((store as { orderShiftHours?: unknown } | undefined)?.orderShiftHours, (store as { businessHours?: unknown } | undefined)?.businessHours),
  };
  const agendaCompleta = !!scheduledDate && (regra.modo === "horario" ? !!scheduledTime : regra.modo === "turno" ? !!scheduledShift : true);
  // Pacote de escolhas (Pedro, 15/10/2026): a vitrine só recebe prepCount quando a loja tem o recurso. Com o pacote, o
  // grupo não tem máximo próprio — quem limita é o máximo do produto (ver o contador abaixo). O peso das opções
  // (Adicionais fracionados) também só chega com o recurso.
  const contandoPreparos = prepCountOn(p?.prepCount);
  const gruposDoProduto = groupsForValidation(Array.isArray(p?.optionGroups) ? p.optionGroups : [], contandoPreparos);
  // Promoção em % no produto de preço base 0: as opções que formam o preço já com o desconto (o servidor cobra assim).
  const groups = gruposComPromocao({ ...p, optionGroups: gruposDoProduto }, "link");
  const ingredients = product?.isFood && Array.isArray(p?.removableIngredients) ? p.removableIngredients : [];

  const { data: capacityData } = useQuery<{
    dailyCapacity: number | null;
    minAdvanceDays: number;
    minAdvanceHours?: number | null;
    bookedCounts: Record<string, number>;
    businessHours: any;
    slotExclusive?: boolean;
    slotIntervalMinutes?: number;
    bookedTimes?: Record<string, string[]>;
    bookedShifts?: Record<string, string[]>;
    orderShiftCapacity?: number | null;
    shiftBookedCounts?: Record<string, Record<string, number>>;
    dayExclusive?: boolean;
    dayLimit?: number | null;
    bookedDays?: Record<string, number>;
  }>({
    queryKey: ["order-capacity", slug, product?.id, calendarMonth, calendarYear],
    queryFn: () => {
      const url = `/api/catalog/${slug}/order-capacity?productId=${product!.id}&month=${calendarMonth + 1}&year=${calendarYear}${selectedUnitId ? `&unitId=${selectedUnitId}` : ''}`;
      return customFetch(url);
    },
    enabled: !!product && !!isOrderBased && !dataUnica,
  });

  useEffect(() => { 
    if (editingItem && product) {
      setQty(editingItem.quantity);
      setNotes(editingItem.notes || "");
      setSelections(
        selectionsByGroupIndex(
          (product as any).optionGroups as OptionGroup[],
          editingItem.selectedOptions,
        ),
      );
      setRemoved(editingItem.removedIngredients || []);
      setScheduledDate(editingItem.scheduledDate || null);
      setScheduledTime(editingItem.scheduledTime || null);
      setScheduledShift((editingItem.scheduledShift as Turno | undefined) || null);
      setPorcaoKg((editingItem as any).weightKg ?? null);
      setPesoStr((editingItem as any).weightKg != null
        ? (unidadeMenor((product as any)?.weightUnit) ? String(Math.round((editingItem as any).weightKg * 1000)) : String((editingItem as any).weightKg).replace(".", ","))
        : "");
      setPesoErro(false);
      if (editingItem.scheduledDate) {
        const [y, m] = editingItem.scheduledDate.split('-').map(Number);
        setCalendarMonth(m - 1);
        setCalendarYear(y);
      }
    } else {
      setQty(1); 
      setNotes(""); 
      setSelections({});
      setRemoved([]);
      setScheduledDate(null);
      setScheduledTime(null);
      setScheduledShift(null);
      // Modal monta com product nulo — o default da porção (a menor) tem que
      // entrar AQUI, quando o produto chega; o useState inicial não o vê.
      const pcs = (product as any)?.soldByWeight ? parsePorcoes((product as any)?.portions) : [];
      setPorcaoKg(pcs.length > 0 ? pcs[0]!.kg : null);
      setPesoStr("");
      setPesoErro(false);
      // O calendário abre no mês do primeiro dia aceito: com «Aceitar encomendas de» no futuro, no mês dele.
      const de = inicioDaEncomenda(product);
      const now = de && de > hojeNaLoja() ? new Date(Number(de.slice(0, 4)), Number(de.slice(5, 7)) - 1, 1) : new Date();
      setCalendarMonth(now.getMonth());
      setCalendarYear(now.getFullYear());
    }
  }, [product, editingItem]);

  const productImages = useMemo(() => {
    const pp = product as any;
    if (Array.isArray(pp?.imageUrls) && pp.imageUrls.length > 0) {
      return pp.imageUrls.filter((u: any): u is string => typeof u === "string" && u.length > 0);
    }
    return pp?.imageUrl ? [pp.imageUrl] : [];
  }, [product]);
  const videoDoProduto = useMemo(() => videoDoCatalogo((product as any)?.video), [product]);
  // Tem o que mostrar no topo (foto ou vídeo).
  const temMidia = productImages.length > 0 || !!videoDoProduto;

  // "Adicionar" com obrigatório faltando NÃO fica desabilitado: o clique rola
  // até o grupo pendente e o destaca (mesmo padrão do "Enviar Pedido por
  // WhatsApp" do checkout). "__schedule" = data/hora de encomenda pendente.
  const [highlightGroup, setHighlightGroup] = useState<string | null>(null);
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scheduleRef = useRef<HTMLDivElement | null>(null);
  const ingredientsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setHighlightGroup(null);
  }, [product]);

  // Quantas escolhas ainda faltam no grupo da posição `i` (0 = grupo resolvido).
  // ⚠️ Mede o PISO, não "tem alguma escolha": com mínimo 2, olhar só o vazio
  // acendia o grupo errado e apagava o destaque em 1 de 2.
  const missingInGroup = (g: any, i: number): number => {
    const sels = selections[optionGroupKey(i)] || [];
    const min = groupMinSelections(g as OptionGroup);
    if (g?.required && sels.length === 0) return Math.max(1, min);
    // O mínimo conta o peso das opções (Adicionais fracionados); sem peso, é a quantidade.
    const soma = groupWeightedCount(g as OptionGroup, sels);
    if (min > 1 && soma < min - 1e-9) return min - soma;
    return 0;
  };

  // Preencheu o que faltava → some o destaque sozinho (igual ao checkout).
  useEffect(() => {
    if (!highlightGroup) return;
    if (highlightGroup === "__schedule") {
      if (agendaCompleta) setHighlightGroup(null);
      return;
    }
    const gi = (groups as any[]).findIndex(
      (_: any, i: number) => optionGroupKey(i) === highlightGroup,
    );
    if (gi >= 0 && missingInGroup((groups as any[])[gi], gi) === 0) setHighlightGroup(null);
  }, [highlightGroup, selections, scheduledDate, scheduledTime, scheduledShift]);

  if (!product) return null;

  const { validated: validatedOptions, optionsTotal } = pickAndPriceOptions(
    groups as OptionGroup[],
    flattenGroupSelections(groups as OptionGroup[], selections),
  );
  const selectedOptions: SelectedOption[] = validatedOptions;

  const detailPromo = getActivePromotion(product, new Date(), "link");
  const perKgPrice = detailPromo.active ? detailPromo.finalPrice : detailPromo.originalPrice;
  const porcaoSel = porcaoKg != null ? porcoes.find((o) => Math.abs(o.kg - porcaoKg) < 0.0005) : undefined;
  const unitProductPrice = (porcoes.length > 0 || pesoLivre) && porcaoKg != null
    ? (porcaoSel?.price != null ? porcaoSel.price : Math.round(perKgPrice * porcaoKg * 100) / 100)
    : perKgPrice;
  const preparos = contandoPreparos ? prepCountDoItem(p.prepCount, groups as OptionGroup[], validatedOptions) : null;
  // O contador fixo no topo da lista disputava espaço com as opções, no celular e no modal do computador (Pedro,
  // 16/10/2026): ele só aparece depois da 1ª escolha que conta no pacote e, com foto, vai para cima dela (escurecida);
  // sem foto, fica no topo da lista.
  // «Cada lista conta separado» (28/10/2026): o contador de cada lista fica dentro dela, não sobre a foto.
  const contadorNaFoto = temMidia && !!preparos && !preparos.porLista && preparos.total > 0;
  const contadorNaLista = !temMidia && !!preparos && !preparos.porLista && preparos.total > 0;
  // Os extras entram no valor das opções da unidade, como no servidor e no carrinho.
  const optionsComExtras = optionsTotal + (preparos?.extrasValue ?? 0);
  const unitPrice = unitProductPrice + optionsComExtras;
  const totalPrice = computeItemTotal({ unitPrice: unitProductPrice, optionsTotal: optionsComExtras, quantity: qty });
  // Escolher mais esta opção passaria do máximo do pacote.
  // Lista ou adicional fora do pacote (`inPackage` false) e pacote sem máximo nunca passam dele.
  // Com «cada lista conta separado», o máximo é o da lista (`gi`).
  const contaDoMaximo = (gi: number) => (preparos?.porLista ? prepCountDaLista(preparos, gi) : preparos);
  const passaDoMaximo = (group: any, optionName: string, gi: number) => {
    const c = contaDoMaximo(gi);
    return c != null && c.max != null && optionInPackage(group, optionName) && c.total + optionWeight(group, optionName) > c.max + 1e-9;
  };

  const allRequiredFilled = isValidGroupSelection(groups as OptionGroup[], selections);

  // Estoque das opções selecionadas limita a QUANTIDADE do item (ex: "M" com
  // 2 em estoque → no máx. 2 unidades), além do estoque do produto/maxPerCart.
  const optionQtyCap = optionStockCap(product, selectedOptions);

  // Critério do grupo atingido (single escolhido / multiple chegou no máximo) →
  // rola sozinho pro próximo passo: grupo seguinte, senão "Remover ingredientes",
  // senão o agendamento. Delay curto pro feedback visual da seleção aparecer
  // antes do scroll. block:"start" alinha o TOPO do card no scroll do modal.
  const scrollToNextStep = (gi: number) => {
    const next = (groups as any[])[gi + 1];
    const target = next
      ? groupRefs.current[optionGroupKey(gi + 1)]
      : (ingredientsRef.current ?? scheduleRef.current);
    if (!target) return;
    setTimeout(() => target.scrollIntoView({ behavior: "smooth", block: "start" }), 180);
  };

  // ⚠️ A seleção é indexada pela POSIÇÃO do grupo (`optionGroupKey`), não pelo
  // nome: o lojista repete o mesmo título em vários grupos ("Acompanhamentos"
  // 4x) e por nome as 4 escolhas caíam no mesmo balde.
  // Na lista que não é obrigatória, tocar de novo na opção marcada desmarca (Pedro, 16/10/2026: «nunca uma escolha
  // única entre um produto só pode travar»); na obrigatória, só troca.
  const toggleSingle = (gKey: string, optionName: string, gi: number, podeDesmarcar: boolean) => {
    const atual = selections[gKey] || [];
    if (podeDesmarcar && atual.length === 1 && atual[0] === optionName) {
      setSelections(prev => ({ ...prev, [gKey]: [] }));
      return;
    }
    const wasEmpty = atual.length === 0;
    setSelections(prev => ({ ...prev, [gKey]: [optionName] }));
    // Só rola na transição vazio→escolhido; trocar de opção depois não re-rola.
    if (wasEmpty) scrollToNextStep(gi);
  };

  // O máximo do grupo conta o peso das opções (Adicionais fracionados); sem peso, é a quantidade.
  const incrementOption = (gKey: string, optionName: string, group: any, gi: number) => {
    const max: number | undefined = group?.maxSelections || undefined;
    const current = selections[gKey] || [];
    const depois = groupWeightedCount(group, [...current, optionName]);
    if (max && depois > max + 1e-9) return;
    setSelections(prev => {
      const cur = prev[gKey] || [];
      if (max && groupWeightedCount(group, [...cur, optionName]) > max + 1e-9) return prev;
      return { ...prev, [gKey]: [...cur, optionName] };
    });
    if (max && depois >= max - 1e-9) scrollToNextStep(gi);
  };

  const decrementOption = (gKey: string, optionName: string) => {
    setSelections(prev => {
      const current = prev[gKey] || [];
      const idx = current.lastIndexOf(optionName);
      if (idx === -1) return prev;
      const updated = [...current];
      updated.splice(idx, 1);
      return { ...prev, [gKey]: updated };
    });
  };

  const toggleRemoved = (ing: string) => {
    setRemoved(prev => prev.includes(ing) ? prev.filter(i => i !== ing) : [...prev, ing]);
  };

  // Aberto antes de a faixa fechar (ou por um link direto): fora do horário de venda não entra no carrinho.
  const foraDoHorario = foraDoHorarioDeVenda(product);
  const handleAdd = () => {
    if (foraDoHorario) return;
    // Pacote de escolhas: o botão já vem desligado abaixo dos inclusos e acima do máximo; aqui é a segunda trava.
    if (preparos && (preparos.missing > 0 || preparos.overMax)) return;
    if (pesoLivre && (porcaoKg == null || porcaoKg <= 0)) {
      setPesoErro(true);
      return;
    }
    if (!allRequiredFilled) {
      const missingIdx = (groups as any[]).findIndex(
        (g: any, i: number) => missingInGroup(g, i) > 0,
      );
      const idx = missingIdx >= 0 ? missingIdx : (groups as any[]).findIndex((g: any) => g.required);
      if (idx >= 0) {
        const key = optionGroupKey(idx);
        setHighlightGroup(key);
        groupRefs.current[key]?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    if (isOrderBased && !dataUnica && !agendaCompleta) {
      setHighlightGroup("__schedule");
      scheduleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    // qty pode ter sido aumentada ANTES de escolher a opção com estoque —
    // clampa na confirmação (o cart store também clampa, defesa dupla).
    const finalQty = optionQtyCap != null ? Math.min(qty, Math.max(1, optionQtyCap)) : qty;
    const comAgenda = isOrderBased && !dataUnica;
    onAdd(
      finalQty,
      notes,
      selectedOptions.length > 0 ? selectedOptions : undefined,
      removed.length > 0 ? removed : undefined,
      comAgenda ? scheduledDate || undefined : undefined,
      comAgenda && regra.modo === "horario" ? scheduledTime || undefined : undefined,
      (porcoes.length > 0 || pesoLivre) && porcaoKg != null ? porcaoKg : undefined,
      comAgenda && regra.modo === "turno" ? scheduledShift || undefined : undefined,
    );
  };

  return (
    <Dialog open={!!product} onOpenChange={onClose}>
      {/* Pedro, 26/10/2026: no celular a janela tem o tamanho do conteúdo (até 90% da tela) e fica presa embaixo, com o
          botão no pé da tela; antes ela tinha sempre 90% da altura, e o produto curto sobrava num vazio. No celular ela
          sobe de baixo (a animação da janela vinha de cima e da direita); no computador, centralizada como antes. */}
      <DialogContent className="sm:max-w-[425px] p-0 overflow-hidden gap-0 rounded-t-3xl sm:rounded-2xl top-auto bottom-0 translate-y-0 sm:top-[50%] sm:bottom-auto sm:translate-y-[-50%] h-auto max-h-[90vh] flex flex-col items-end align-bottom [&>button.absolute]:hidden max-sm:data-[state=open]:slide-in-from-bottom max-sm:data-[state=open]:slide-in-from-right-0 max-sm:data-[state=open]:zoom-in-100 max-sm:data-[state=closed]:slide-out-to-bottom max-sm:data-[state=closed]:slide-out-to-right-0 max-sm:data-[state=closed]:zoom-out-100" data-testid="janela-produto">
        {temMidia && (
          // Foto em 16:9 (Pedro, 15/10/2026): mais espaço para o nome e as opções (antes, altura fixa de 256 px). Sem o
          // overflow-hidden, a foto mais alta que 16:9 esticava a caixa (o mínimo de altura vira o da imagem).
          <ProductImageCarousel
            images={productImages}
            video={videoDoProduto}
            alt={product.name}
            onClose={onClose}
            containerClassName="w-full aspect-video overflow-hidden bg-muted relative shrink-0"
            sobre={contadorNaFoto && preparos ? (
              // Sem captura de toque: o arraste e as setas da foto continuam passando.
              <>
                <div className="absolute inset-0 bg-black/80 pointer-events-none animate-in fade-in duration-200" />
                <div className="absolute inset-0 flex items-center px-14 pointer-events-none" data-testid="prep-counter-foto">
                  <div className="w-full animate-in fade-in zoom-in-95 duration-200">
                    <ContadorDePreparos c={preparos} />
                  </div>
                </div>
              </>
            ) : undefined}
          />
        )}
        <div className="p-6 flex-1 overflow-y-auto w-full">
          {!temMidia && (
            <Button variant="ghost" size="icon" className="absolute top-4 right-4" onClick={onClose}><X className="w-4 h-4" /></Button>
          )}
          <h2 className="text-2xl font-bold font-display leading-tight">{product.name}</h2>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {(() => {
              if (pricesByOptions(product)) {
                const ap = aPartirDeComPromocao(product, "link", gruposDoProduto as OptionGroup[]);
                if (ap) {
                  return ap.original != null ? (
                    <>
                      <p className="text-xl font-bold text-accent">
                        <span className="text-sm font-medium text-muted-foreground">A partir de </span>
                        {formatCurrency(ap.valor)}
                      </p>
                      <p className="text-sm text-muted-foreground line-through">{formatCurrency(ap.original)}</p>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-accent-foreground bg-accent px-2 py-0.5 rounded-full">{store?.promotionBadgeText || "Promo"}</span>
                    </>
                  ) : (
                    <p className="text-xl font-bold text-primary">
                      <span className="text-sm font-medium text-muted-foreground">A partir de </span>
                      {formatCurrency(ap.valor)}
                    </p>
                  );
                }
              }
              if (detailPromo.active) {
                return (
                  <>
                    <p className="text-sm text-muted-foreground line-through">{formatCurrency(detailPromo.originalPrice)}</p>
                    <p className="text-xl font-bold text-accent">{formatCurrency(detailPromo.finalPrice)}</p>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-accent-foreground bg-accent px-2 py-0.5 rounded-full">{store?.promotionBadgeText || "Promo"}</span>
                    {detailPromo.discountPercent > 0 && (
                      <span className="text-[10px] font-bold text-accent bg-accent/10 border border-accent/30 px-2 py-0.5 rounded-full">−{detailPromo.discountPercent}%</span>
                    )}
                  </>
                );
              }
              return <p className="text-xl font-bold text-primary">{(product as any).soldByWeight ? precoPeso(Number(product.price), (product as any).weightUnit).valor : formatCurrency(product.price)}{(product as any).soldByWeight ? <span className="text-sm font-semibold text-muted-foreground">{precoPeso(Number(product.price), (product as any).weightUnit).sufixo}</span> : null}</p>;
            })()}
            {p.servings && <span className="text-sm text-secondary bg-secondary/15 px-2.5 py-1 rounded-full font-medium">Serve {p.servings} {p.servings === 1 ? 'pessoa' : 'pessoas'}</span>}
            {p.slices > 0 && <span className="text-sm text-secondary bg-secondary/15 px-2.5 py-1 rounded-full font-medium">{p.slices} {p.slices === 1 ? 'fatia' : 'fatias'}</span>}
            {p.isVegan && <span className="text-sm font-semibold text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full flex items-center gap-1"><Leaf className="w-3.5 h-3.5" />Vegano</span>}
          </div>
          {detailPromo.active && detailPromo.endsLabel && (
            <p className="text-xs text-accent/80 font-medium mt-1">⏱ {detailPromo.endsLabel}</p>
          )}
          {product.description && <p className="text-muted-foreground mt-4 leading-relaxed"><LinkifiedText text={product.description} /></p>}

          {pesoLivre && (
            <div className="mt-6">
              <div className="flex items-baseline justify-between">
                <h3 className="font-bold">{menor === "ml" ? "Quanto você quer?" : "Qual peso você quer?"} <span className="font-normal text-muted-foreground text-sm">({menor ?? "kg"})</span></h3>
              </div>
              <Input
                value={pesoStr}
                inputMode="decimal"
                placeholder={menor === "ml" ? "Quantidade em ml — ex: 5" : emGramas ? "Peso em gramas — ex: 500" : "Peso em kg — ex: 0,5"}
                className={`mt-3 ${pesoErro ? "ring-2 ring-destructive" : ""}`}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^\d.,]/g, "");
                  setPesoStr(v);
                  setPesoErro(false);
                  const bruto = parseFloat(v.replace(",", "."));
                  const w = Math.round((emGramas ? bruto / 1000 : bruto) * 1000) / 1000;
                  setPorcaoKg(Number.isFinite(w) && w >= 0.001 && w <= 999 ? w : null);
                }}
                data-testid="storefront-weight-input"
              />
              {porcaoKg != null ? (
                <p className="text-xs text-muted-foreground mt-1.5">{menor ? `${Math.round(porcaoKg * 1000)} ${menor}` : `${String(porcaoKg).replace(".", ",")} kg`} × {precoPeso(perKgPrice, unidadeDoPeso).valor}{precoPeso(perKgPrice, unidadeDoPeso).sufixo} = <strong className="text-foreground">{formatCurrency(unitProductPrice)}</strong></p>
              ) : pesoErro ? (
                <p className="text-xs text-destructive mt-1.5">{menor === "ml" ? "Informe a quantidade pra continuar." : "Informe o peso pra continuar."}</p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1.5">{menor === "ml" ? "O valor sai da quantidade em ml." : "O valor sai do peso: o lojista pesa e confirma."}</p>
              )}
            </div>
          )}

          {porcoes.length > 0 && (
            <div className="mt-6 space-y-3">
              {/* Mesmo padrão visual do grupo de opção single (pedido do
                  Pedro: porções "como os adicionais", não pílulas). */}
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-bold">Porção</h3>
                  <p className="text-xs text-muted-foreground">Escolha uma opção</p>
                </div>
                <span className="text-xs font-bold text-destructive bg-destructive/10 px-2 py-0.5 rounded">Obrigatório</span>
              </div>
              <div className="space-y-1.5">
                {porcoes.map((o) => {
                  const ativo = porcaoKg != null && Math.abs(porcaoKg - o.kg) < 0.0005;
                  const precoPorcao = o.price != null ? o.price : Math.round(perKgPrice * o.kg * 100) / 100;
                  return (
                    <button
                      key={o.kg}
                      type="button"
                      onClick={() => setPorcaoKg(o.kg)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left ${ativo ? 'border-accent bg-accent/10' : 'border-border hover:bg-muted/50'}`}
                      data-testid={`portion-${o.kg}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${ativo ? 'border-accent bg-accent' : 'border-muted-foreground/30'}`}>
                          {ativo && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                        </div>
                        <span className="font-medium text-sm">{pesoLabel(o.kg, unidadeDoPeso)}</span>
                      </div>
                      <span className="text-sm text-muted-foreground font-medium">{formatCurrency(precoPorcao)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {contadorNaLista && preparos && (
            <div className="sticky top-0 z-10 -mx-6 mt-4 bg-background px-6 pb-2 pt-2" data-testid="prep-counter">
              <ContadorDePreparos c={preparos} />
            </div>
          )}

          {groups.length > 0 && (
            <div className="mt-6 space-y-5">
              {groups.map((group: any, gi: number) => {
                const gKey = optionGroupKey(gi);
                const sel = selections[gKey] || [];
                const isHighlighted = highlightGroup === gKey;
                const opcoes = opcoesDoGrupo(group, sel);
                if (esconderEsgotadas && opcoes.length === 0 && groupMinSelections(group as OptionGroup) === 0) return null;
                return (
                  <div
                    key={gi}
                    ref={(el) => { groupRefs.current[gKey] = el; }}
                    className={`space-y-3 rounded-2xl border-2 p-3.5 scroll-mt-3 transition-all ${isHighlighted ? "border-destructive/50 bg-destructive/5" : "border-border/80 bg-muted/20"}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-sm">{group.name}</h3>
                        {group.selectionType === "multiple" && group.maxSelections && (() => {
                          // Piso do grupo (combo que exige os N sabores): o texto
                          // vira "escolha N" e conta o que falta. Com peso nas opções (Adicionais fracionados), conta
                          // o peso: «Escolha até 4 · 3½ de 4».
                          const min = groupMinSelections(group as OptionGroup);
                          const comPeso = groupHasWeights(group as OptionGroup);
                          const soma = groupWeightedCount(group as OptionGroup, sel);
                          const faltam = min > 1 ? Math.max(0, min - soma) : 0;
                          return (
                            <p className={`text-xs ${faltam > 0 ? "text-destructive font-semibold" : "text-muted-foreground"}`} data-testid={`group-count-${gi}`}>
                              {min > 1 ? `Escolha ${min}` : `Escolha até ${group.maxSelections}`}
                              {comPeso ? ` · ${formatPreparos(soma)} de ${group.maxSelections}` : ` — ${sel.length}/${group.maxSelections} selecionados`}
                              {faltam > 0 ? ` · faltam ${formatPreparos(faltam)}` : ""}
                            </p>
                          );
                        })()}
                        {group.selectionType === "multiple" && !group.maxSelections && (() => {
                          // Sem teto o piso continua valendo — dizer "escolha
                          // quantos quiser" e recusar em 1 de 2 era mentira.
                          const min = groupMinSelections(group as OptionGroup);
                          // «Cada lista conta separado»: no lugar do «Escolha quantos quiser», a regra da lista (abaixo).
                          if (min <= 1 && prepCountDaLista(preparos, gi)) return null;
                          if (min <= 1) return <p className="text-xs text-muted-foreground">Escolha quantos quiser</p>;
                          const faltam = Math.max(0, min - sel.length);
                          return (
                            <p className={`text-xs ${faltam > 0 ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                              Escolha pelo menos {min} — {sel.length} {sel.length === 1 ? "selecionado" : "selecionados"}
                              {faltam > 0 ? ` · ${faltam === 1 ? "falta" : "faltam"} ${faltam}` : ""}
                            </p>
                          );
                        })()}
                        {group.selectionType === "single" && (
                          <p className="text-xs text-muted-foreground">Escolha uma opção</p>
                        )}
                      </div>
                      {group.required && <span className="text-xs font-bold text-destructive bg-destructive/10 px-2 py-0.5 rounded">Obrigatório</span>}
                    </div>
                    {(() => {
                      // «Cada lista conta separado» (Pedro, 28/10/2026): a regra da lista até a 1ª escolha nela; depois, o
                      // contador da lista (o mesmo da foto, dentro da lista).
                      const lc = prepCountDaLista(preparos, gi);
                      if (!lc) return null;
                      if (lc.total <= 0) {
                        const r = prepCardResumo({ ...lc, porLista: false }, formatCurrency);
                        return (
                          <p className="text-xs text-muted-foreground" data-testid={`prep-lista-regra-${gi}`}>
                            <b className="font-semibold text-foreground">{r.pacote}</b>
                            {r.extras && <> · {r.extras}</>}
                          </p>
                        );
                      }
                      return <div data-testid={`prep-lista-${gi}`}><ContadorDePreparos c={lc} /></div>;
                    })()}
                    {isHighlighted && (() => {
                      const min = groupMinSelections(group as OptionGroup);
                      const faltam = missingInGroup(group, gi);
                      return (
                        <p className="text-xs font-semibold text-destructive">
                          {min > 1
                            ? `${faltam <= 1 ? "Falta" : "Faltam"} ${formatPreparos(faltam)} de ${min} escolhas aqui pra continuar.`
                            : "Escolha uma opção aqui pra continuar."}
                        </p>
                      );
                    })()}
                    <div className="space-y-1.5">
                      {opcoes.map((opt: any, oi: number) => {
                        const optName = displayName(opt);
                        const optCount = sel.filter((n: string) => n === opt.name).length;
                        const isSelected = optCount > 0;
                        // Adicionais fracionados: a opção conta o peso dela no máximo do grupo (e no pacote).
                        const peso = optionWeight(group as OptionGroup, opt.name);
                        // Na escolha única o peso não mexe no limite do grupo (é sempre uma): lá ele só vale para o pacote.
                        const multipla = group.selectionType === "multiple";
                        const rotuloPeso = ((contandoPreparos && optionInPackage(group as OptionGroup, opt.name)) || (multipla && (!!group.maxSelections || groupMinSelections(group as OptionGroup) > 1))) ? optionWeightLabel(peso) : null;
                        const passaGrupo = multipla && !!group.maxSelections && groupWeightedCount(group as OptionGroup, sel) + peso > group.maxSelections + 1e-9;
                        const passaM = passaDoMaximo(group, opt.name, gi);
                        const atMax = passaGrupo || passaM;
                        // O item que faria passar do máximo fica cinza, com o motivo (no grupo sem peso, o «4/4» já diz).
                        const motivoMax = !isSelected && (passaM || (passaGrupo && groupHasWeights(group as OptionGroup))) ? (
                          <span className="text-xs text-muted-foreground font-medium whitespace-nowrap shrink-0">
                            passa de {formatPreparos(passaM ? contaDoMaximo(gi)?.max ?? 0 : group.maxSelections)}
                          </span>
                        ) : null;
                        const seloPeso = rotuloPeso ? (
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-foreground whitespace-nowrap shrink-0" data-testid="option-weight">{rotuloPeso}</span>
                        ) : null;
                        const unavailable = isOptionUnavailable(opt);
                        const lowStock = lowStockLabel(opt);
                        const cap = stockCap(opt);
                        const atStockCap = cap != null && optCount >= cap;

                        if (group.selectionType === "single") {
                          return (
                            <button
                              key={oi}
                              type="button"
                              onClick={() => { if (!unavailable && !(passaM && sel.length === 0)) toggleSingle(gKey, opt.name, gi, groupMinSelections(group as OptionGroup) === 0); }}
                              disabled={unavailable || (passaM && sel.length === 0)}
                              className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed ${isSelected ? 'border-accent bg-accent/10' : 'border-border hover:bg-muted/50'}`}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-5 h-5 rounded-full shrink-0 border-2 flex items-center justify-center ${isSelected ? 'border-accent bg-accent' : 'border-muted-foreground/30'}`}>
                                  {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                                </div>
                                <span className={`font-medium text-sm ${unavailable ? 'line-through' : ''}`}>{optName}</span>
                                {seloPeso}
                                {unavailable && <span className="text-xs text-destructive font-medium whitespace-nowrap shrink-0">Esgotado</span>}
                                {!unavailable && lowStock && <span className="text-xs text-amber-600 font-medium whitespace-nowrap shrink-0">{lowStock}</span>}
                                {sel.length === 0 && motivoMax}
                              </div>
                              {opt.price > 0 && <span className="text-sm text-muted-foreground font-medium whitespace-nowrap shrink-0 ml-2">+ {formatCurrency(opt.price)}</span>}
                            </button>
                          );
                        }

                        // "Permitir apenas 1 de cada": a opção vira seletor
                        // liga/desliga (checkbox) em vez do contador +/-.
                        if (group.onePerOption) {
                          return (
                            <button
                              key={oi}
                              type="button"
                              onClick={() => {
                                if (unavailable) return;
                                if (isSelected) { decrementOption(gKey, opt.name); return; }
                                if (atMax || atStockCap) return;
                                incrementOption(gKey, opt.name, group, gi);
                              }}
                              disabled={unavailable || (atMax && !isSelected)}
                              className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed ${isSelected ? 'border-accent bg-accent/10' : 'border-border hover:bg-muted/50'}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${isSelected ? 'border-accent bg-accent' : 'border-muted-foreground/30'}`}>
                                  {isSelected && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <span className={`font-medium text-sm ${unavailable ? 'line-through' : ''}`}>{optName}</span>
                                {seloPeso}
                                {unavailable && <span className="text-xs text-destructive font-medium whitespace-nowrap shrink-0">Esgotado</span>}
                                {!unavailable && lowStock && <span className="text-xs text-amber-600 font-medium whitespace-nowrap shrink-0">{lowStock}</span>}
                                {motivoMax}
                              </div>
                              {opt.price > 0 && <span className="text-sm text-muted-foreground font-medium whitespace-nowrap shrink-0 ml-2">+ {formatCurrency(opt.price)}</span>}
                            </button>
                          );
                        }

                        return (
                          <button
                            type="button"
                            key={oi}
                            onClick={() => { if (!unavailable && !(atMax && !isSelected) && !atStockCap) incrementOption(gKey, opt.name, group, gi); }}
                            disabled={unavailable || ((atMax || atStockCap) && !isSelected)}
                            className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed ${isSelected ? 'border-accent bg-accent/10' : 'border-border'}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`font-medium text-sm ${unavailable ? 'line-through' : ''}`}>{optName}</span>
                              {seloPeso}
                              {unavailable && <span className="text-xs text-destructive font-medium whitespace-nowrap shrink-0">Esgotado</span>}
                              {!unavailable && lowStock && <span className="text-xs text-amber-600 font-medium whitespace-nowrap shrink-0">{lowStock}</span>}
                              {opt.price > 0 && <span className="text-xs text-muted-foreground font-medium whitespace-nowrap">+ {formatCurrency(opt.price)}</span>}
                              {motivoMax}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {optCount > 0 ? (
                                <>
                                  <span role="button" onClick={(e) => { e.stopPropagation(); decrementOption(gKey, opt.name); }} className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors">
                                    <Minus className="w-3.5 h-3.5" />
                                  </span>
                                  <span className="w-8 text-center text-sm font-bold">{optCount}</span>
                                </>
                              ) : null}
                              <span
                                className={`w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center ${atStockCap || (atMax && !isSelected) ? 'opacity-30' : ''}`}
                              >
                                <Plus className="w-3.5 h-3.5 text-accent" />
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {ingredients.length > 0 && (
            <div ref={ingredientsRef} className="mt-5 space-y-3 rounded-2xl border-2 border-border/80 bg-muted/20 p-3.5 scroll-mt-3">
              <h3 className="font-bold text-sm">Remover ingredientes</h3>
              <div className="space-y-1.5">
                {ingredients.map((ing: string, i: number) => {
                  const isRemoved = removed.includes(ing);
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => toggleRemoved(ing)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${isRemoved ? 'border-destructive/50 bg-destructive/5' : 'border-border hover:bg-muted/50'}`}
                    >
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${isRemoved ? 'border-destructive bg-destructive' : 'border-muted-foreground/30'}`}>
                        {isRemoved && <X className="w-3 h-3 text-white" />}
                      </div>
                      <span className={`text-sm ${isRemoved ? 'line-through text-muted-foreground' : 'font-medium'}`}>{ing}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {isOrderBased && dataUnica && (
            <div className="mt-6 rounded-xl border border-primary/20 bg-primary/5 p-3 text-sm text-primary space-y-1" data-testid="produto-data-no-fim">
              <div className="flex items-center gap-2 font-semibold">
                <CalendarDays className="w-4 h-4 shrink-0" />
                Você escolhe a data e o horário no fim do pedido.
              </div>
              {(p.minAdvanceHours || (p.minAdvanceDays ?? 0) > 0 || isAvailabilityConfigActive(p.availabilityConfig) || limiteDaEncomenda(p) || inicioDaEncomenda(p)) && (
                <p className="text-xs text-primary/80">
                  {p.minAdvanceHours
                    ? `Prazo mínimo de ${p.minAdvanceHours} ${p.minAdvanceHours === 1 ? "hora" : "horas"} de antecedência.`
                    : (p.minAdvanceDays ?? 0) > 0
                      ? `Prazo mínimo de ${p.minAdvanceDays} ${p.minAdvanceDays === 1 ? "dia" : "dias"} de antecedência.`
                      : ""}
                  {isAvailabilityConfigActive(p.availabilityConfig) ? ` Feito: ${summarizeAvailabilityConfig(p.availabilityConfig)}.` : ""}
                  {janelaDaEncomenda(inicioDaEncomenda(p), limiteDaEncomenda(p)) ? ` Encomendas ${janelaDaEncomenda(inicioDaEncomenda(p), limiteDaEncomenda(p))}.` : ""}
                </p>
              )}
            </div>
          )}

          {isOrderBased && !dataUnica && isAvailabilityConfigActive(p.availabilityConfig) && (() => {
            // Limite de encomendas por dia: o dia cheio não entra nas sugestões (a ocupação vem até 60 dias à frente).
            const next = proximasDatasEncomenda(p.availabilityConfig as ProductAvailabilityConfig, p.minAdvanceDays ?? 0, p.minAdvanceHours, 10, new Date(), 60, regra, limiteDaEncomenda(p), inicioDaEncomenda(p))
              .filter((d) => !diaOcupado(capacityData, d))
              .slice(0, 5);
            const handlePickDate = (ds: string) => {
              const [yStr, mStr] = ds.split("-");
              const y = parseInt(yStr, 10);
              const m = parseInt(mStr, 10) - 1;
              if (!Number.isNaN(y) && !Number.isNaN(m)) {
                setCalendarMonth(m);
                setCalendarYear(y);
              }
              setScheduledDate(ds);
              setScheduledTime(null);
              setScheduledShift(null);
            };
            return (
              <div className="mt-6 rounded-xl border border-primary/20 bg-primary/5 p-3">
                <div className="flex items-center gap-2 text-primary text-sm font-semibold">
                  <CalendarClock className="w-4 h-4" />
                  Próximas datas disponíveis
                </div>
                {next.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {next.map(d => {
                      const isSelected = scheduledDate === d;
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() => handlePickDate(d)}
                          aria-pressed={isSelected}
                          className={`text-xs px-2 py-1 rounded-full font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                            isSelected
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background text-primary border-primary/30 hover:bg-primary/10"
                          }`}
                        >
                          {formatAvailabilityDateLabel(d)}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-primary mt-1">Sem datas disponíveis nos próximos 60 dias.</p>
                )}
              </div>
            );
          })()}

          {isOrderBased && !dataUnica && (
            <div
              ref={scheduleRef}
              className={`rounded-xl p-2 -m-2 scroll-mt-3 transition-all ${highlightGroup === "__schedule" ? "ring-2 ring-red-300 bg-destructive/5" : ""}`}
            >
              {highlightGroup === "__schedule" && (
                <p className="text-xs font-semibold text-destructive mb-2">
                  {regra.modo === "turno" ? "Escolha a data e o turno pra continuar." : regra.modo === "data" ? "Escolha a data pra continuar." : "Escolha a data e o horário pra continuar."}
                </p>
              )}
              <OrderSchedulePicker
                regra={regra}
                scheduledDate={scheduledDate}
                scheduledTime={scheduledTime}
                scheduledShift={scheduledShift}
                onDateChange={(d) => { setScheduledDate(d); setScheduledShift(null); }}
                onTimeChange={setScheduledTime}
                onShiftChange={setScheduledShift}
                calendarMonth={calendarMonth}
                calendarYear={calendarYear}
                onMonthChange={(m, y) => { setCalendarMonth(m); setCalendarYear(y); }}
                capacityData={capacityData}
                availabilityConfig={p.availabilityConfig as ProductAvailabilityConfig | null | undefined}
                ate={limiteDaEncomenda(p) ?? limiteDaEncomenda(capacityData)}
                de={inicioDaEncomenda(p) ?? inicioDaEncomenda(capacityData)}
              />
            </div>
          )}

          {maxPerCart != null && maxPerCart > 0 && (
            <div className="flex items-start gap-2 rounded-xl bg-primary/5 border border-primary/15 p-3 text-sm text-primary">
              <ShoppingBag className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Quantidade máxima por entrega: <strong>{maxPerCart}</strong></span>
            </div>
          )}

        </div>

        {/* Sem seletor de quantidade aqui: ao lado dos steppers das opções
            ("escolha quantos quiser") o cliente não sabia qual quantidade
            estava mexendo. Quantidade se ajusta no carrinho, onde o "+" duplica
            a linha com as MESMAS opções já escolhidas. */}
        <div className="p-4 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-border/50 bg-background shrink-0 w-full">
          <Button
            className="w-full h-12 rounded-xl text-lg font-bold shadow-lg shadow-primary/25"
            onClick={handleAdd}
            disabled={!!foraDoHorario || (!!preparos && (preparos.missing > 0 || preparos.overMax))}
            data-testid="product-add-button"
          >
            {foraDoHorario
              ? foraDoHorario
              : preparos && preparos.missing > 0
              ? prepFaltamTexto(preparos)
              : preparos?.overMax
                ? (() => {
                    const lista = preparos.listas?.find((l) => l.overMax);
                    return lista ? `Passou de ${qtdComNome(lista.max ?? 0, lista.unitName)} em ${lista.nome}` : `Passou de ${qtdComNome(preparos.max ?? 0, preparos.unitName)}`;
                  })()
                : `${editingItem ? 'Salvar' : 'Adicionar'} • ${formatCurrency(totalPrice)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const MONTH_NAMES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const DAY_NAMES = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
// Grade padrão (produto sem janelas de disponibilidade): 08:00–20:00 no passo
// escolhido pela loja (30 é o histórico).
function defaultTimeSlots(stepMinutes: number): string[] {
  const step = stepMinutes > 0 ? stepMinutes : 30;
  const out: string[] = [];
  for (let t = 8 * 60; t <= 20 * 60; t += step) {
    out.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
  }
  return out;
}

// «Aceitar encomendas» de/até do produto (26/10/2026): "AAAA-MM-DD" ou null. O catálogo e o order-capacity mandam os campos.
function limiteDaEncomenda(fonte: unknown): string | null {
  const v = (fonte as { orderUntilDate?: unknown } | null | undefined)?.orderUntilDate;
  return typeof v === "string" && v.length >= 10 ? v.slice(0, 10) : null;
}
function inicioDaEncomenda(fonte: unknown): string | null {
  const v = (fonte as { orderFromDate?: unknown } | null | undefined)?.orderFromDate;
  return typeof v === "string" && v.length >= 10 ? v.slice(0, 10) : null;
}

// Produto por encomenda sem a disponibilidade limitada: o primeiro dia possível (antecedência ou início) já passou do
// limite. O início no futuro sozinho não fecha o produto.
function encomendaPassouDoLimite(p: any): boolean {
  const ate = limiteDaEncomenda(p);
  if (!ate) return false;
  const prazo = p.minAdvanceHours ? primeiroMomentoEncomenda(p.minAdvanceHours).data : somaDias(hojeNaLoja(), Math.max(0, p.minAdvanceDays ?? 0));
  const de = inicioDaEncomenda(p);
  return (de && de > prazo ? de : prazo) > ate;
}

function OrderSchedulePicker({ regra, scheduledDate, scheduledTime, scheduledShift, onDateChange, onTimeChange, onShiftChange, calendarMonth, calendarYear, onMonthChange, capacityData, availabilityConfig, ate = null, de = null }: {
  regra: RegraDaEncomenda;
  scheduledDate: string | null;
  scheduledTime: string | null;
  scheduledShift: Turno | null;
  onDateChange: (d: string | null) => void;
  onTimeChange: (t: string | null) => void;
  onShiftChange: (t: Turno | null) => void;
  calendarMonth: number;
  calendarYear: number;
  onMonthChange: (m: number, y: number) => void;
  capacityData?: { dailyCapacity: number | null; minAdvanceDays: number; minAdvanceHours?: number | null; bookedCounts: Record<string, number>; businessHours: any; slotExclusive?: boolean; slotIntervalMinutes?: number; bookedTimes?: Record<string, string[]>; bookedShifts?: Record<string, string[]>; orderShiftCapacity?: number | null; shiftBookedCounts?: Record<string, Record<string, number>>; dayExclusive?: boolean; dayLimit?: number | null; bookedDays?: Record<string, number> } | null;
  availabilityConfig?: ProductAvailabilityConfig | null;
  ate?: string | null;
  de?: string | null;
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minAdvance = capacityData?.minAdvanceDays ?? 0;
  const minAdvanceHours = capacityData?.minAdvanceHours ?? null;
  // Em horas: primeiro dia/horário = agora + H no relógio da loja; o dia começa nele e os horários antes dele
  // somem. Em dias: o calendário, como sempre.
  const primeiro = minAdvanceHours ? primeiroMomentoEncomenda(minAdvanceHours) : null;
  const minDate = new Date(today);
  if (primeiro) {
    const [py, pm, pd] = primeiro.data.split("-").map(Number);
    minDate.setFullYear(py, pm - 1, pd);
  } else {
    minDate.setDate(minDate.getDate() + minAdvance);
  }

  const availabilityActive = isAvailabilityConfigActive(availabilityConfig);
  const slotStep = capacityData?.slotIntervalMinutes ?? 30;
  const slotsDoDia = (ds: string) =>
    availabilityActive ? getAvailableTimeSlotsForDate(availabilityConfig, ds, slotStep) : defaultTimeSlots(slotStep);
  // Turno (12/10/2026): os ligados que o dia cobre e que começam depois do prazo; o reservado (uma por turno) e o
  // que bateu o limite por turno do produto saem riscados, como o horário ocupado.
  const turnosVisiveis = (ds: string): Turno[] => turnosDoDia(availabilityConfig, ds, regra, primeiro);
  const turnoOcupado = (ds: string, t: Turno): boolean => {
    if (capacityData?.slotExclusive && (capacityData.bookedShifts?.[ds] ?? []).includes(t)) return true;
    const limite = capacityData?.orderShiftCapacity;
    return !!limite && (capacityData?.shiftBookedCounts?.[ds]?.[t] ?? 0) >= limite;
  };
  const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(calendarYear, calendarMonth, 1).getDay();

  const isDateDisabled = (day: number) => {
    const date = new Date(calendarYear, calendarMonth, day);
    date.setHours(0, 0, 0, 0);
    if (date < minDate) return true;
    const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    if (ate && dateStr > ate) return true;
    if (de && dateStr < de) return true;
    if (availabilityActive && !isDateAllowed(availabilityConfig, dateStr)) return true;
    if (regra.modo === "turno") {
      if (!turnosVisiveis(dateStr).some((t) => !turnoOcupado(dateStr, t))) return true;
    } else if (regra.modo === "data") {
      if (!diaServeSoData(availabilityConfig, dateStr, primeiro)) return true;
    } else if (primeiro && dateStr === primeiro.data && !slotsDoDia(dateStr).some((t) => t >= primeiro.hora)) {
      return true;
    }
    if (capacityData?.dailyCapacity) {
      const booked = capacityData.bookedCounts[dateStr] || 0;
      if (booked >= capacityData.dailyCapacity) return true;
    }
    // Limite de encomendas por dia: o dia cheio fica fechado, em qualquer modo.
    if (diaOcupado(capacityData, dateStr)) return true;
    return false;
  };

  const visibleTimeSlots = (scheduledDate ? slotsDoDia(scheduledDate) : defaultTimeSlots(slotStep))
    .filter((t) => !(primeiro && scheduledDate === primeiro.data && t < primeiro.hora));
  // Horário exclusivo por loja: ocupado fica desabilitado (e o realtime do
  // catálogo revalida esta query quando entra pedido — a grade atualiza só).
  const takenTimes = (capacityData?.slotExclusive && scheduledDate)
    ? new Set(capacityData?.bookedTimes?.[scheduledDate] ?? [])
    : new Set<string>();

  const getDateStr = (day: number) => `${calendarYear}-${String(calendarMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  const goToPrevMonth = () => {
    if (calendarMonth === 0) onMonthChange(11, calendarYear - 1);
    else onMonthChange(calendarMonth - 1, calendarYear);
  };
  const goToNextMonth = () => {
    if (calendarMonth === 11) onMonthChange(0, calendarYear + 1);
    else onMonthChange(calendarMonth + 1, calendarYear);
  };

  const mesMostrado = `${calendarYear}-${String(calendarMonth + 1).padStart(2, "0")}`;
  // Antes do mês do início também não há o que escolher.
  const isPrevDisabled = (calendarYear === today.getFullYear() && calendarMonth <= today.getMonth()) || (!!de && de.slice(0, 7) >= mesMostrado);
  // Depois do mês do limite não há o que escolher: a seta para.
  const isNextDisabled = !!ate && ate.slice(0, 7) <= mesMostrado;

  const formatSelectedDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    const dayName = DAY_NAMES[dt.getDay()];
    return `${dayName}, ${d} de ${MONTH_NAMES[m - 1]}`;
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center gap-2">
        <CalendarDays className="w-4 h-4 text-primary" />
        <h3 className="font-bold text-sm">Escolha a data de retirada/entrega</h3>
      </div>
      {minAdvanceHours ? (
        <p className="text-xs text-muted-foreground">Prazo mínimo de {minAdvanceHours} {minAdvanceHours === 1 ? 'hora' : 'horas'} de antecedência.</p>
      ) : minAdvance > 0 && (
        <p className="text-xs text-muted-foreground">Prazo mínimo de {minAdvance} {minAdvance === 1 ? 'dia' : 'dias'} de antecedência.</p>
      )}
      {janelaDaEncomenda(de, ate) && <p className="text-xs text-muted-foreground" data-testid="encomenda-ate">Encomendas {janelaDaEncomenda(de, ate)}.</p>}

      <div className="border border-border/50 rounded-xl p-3">
        <div className="flex items-center justify-between mb-3">
          <button type="button" onClick={goToPrevMonth} disabled={isPrevDisabled} className="p-1 rounded hover:bg-muted disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span className="font-semibold text-sm">{MONTH_NAMES[calendarMonth]} {calendarYear}</span>
          <button type="button" onClick={goToNextMonth} disabled={isNextDisabled} className="p-1 rounded hover:bg-muted disabled:opacity-30" aria-label="Próximo mês"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center">
          {DAY_NAMES.map(d => <div key={d} className="text-xs font-medium text-muted-foreground py-1">{d}</div>)}
          {Array.from({ length: firstDayOfWeek }).map((_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateStr = getDateStr(day);
            const disabled = isDateDisabled(day);
            const selected = scheduledDate === dateStr;
            const isToday = calendarYear === today.getFullYear() && calendarMonth === today.getMonth() && day === today.getDate();
            return (
              <button
                key={day}
                type="button"
                disabled={disabled}
                onClick={() => { onDateChange(dateStr); onTimeChange(null); }}
                data-testid={`encomenda-dia-${dateStr}`}
                className={`w-full aspect-square rounded-lg text-sm font-medium transition-all
                  ${selected ? 'bg-accent text-accent-foreground font-bold' : ''}
                  ${!selected && !disabled ? 'hover:bg-accent/10' : ''}
                  ${disabled ? 'text-muted-foreground/30 cursor-not-allowed' : 'cursor-pointer'}
                  ${isToday && !selected ? 'ring-1 ring-primary/50' : ''}
                `}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>

      {regra.modo === "data" && (
        <p className="text-xs text-muted-foreground" data-testid="encomenda-so-data">O horário você combina com a loja pelo WhatsApp.</p>
      )}

      {scheduledDate && regra.modo === "turno" && (
        <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            <h3 className="font-bold text-sm">Turno</h3>
          </div>
          <p className="text-xs text-muted-foreground">{formatSelectedDate(scheduledDate)}</p>
          {turnosVisiveis(scheduledDate).length === 0 ? (
            <p className="text-xs text-muted-foreground">Nenhum turno disponível para esta data.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {turnosVisiveis(scheduledDate).map((t) => {
                const ocupado = turnoOcupado(scheduledDate, t);
                const escolhido = scheduledShift === t;
                return (
                  <button
                    key={t}
                    type="button"
                    disabled={ocupado}
                    onClick={() => onShiftChange(t)}
                    data-testid={`encomenda-turno-${t}`}
                    className={`py-2 px-2 rounded-lg border transition-all flex flex-col items-center gap-0.5
                      ${ocupado ? 'border-border text-muted-foreground/50 line-through cursor-not-allowed bg-muted/40'
                        : escolhido ? 'bg-accent text-accent-foreground border-accent' : 'border-border hover:bg-accent/10 hover:border-accent/30'}
                    `}
                  >
                    <span className="text-sm font-semibold">{NOME_DO_TURNO[t]}</span>
                    <span className={`text-[11px] ${escolhido ? 'text-accent-foreground/90' : 'text-muted-foreground'}`}>{faixaDoTurnoTexto(regra.horarios[t])}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {scheduledDate && regra.modo === "horario" && (
        <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            <h3 className="font-bold text-sm">Horário preferido</h3>
          </div>
          <p className="text-xs text-muted-foreground">{formatSelectedDate(scheduledDate)}</p>
          {visibleTimeSlots.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nenhum horário disponível para esta data.</p>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {visibleTimeSlots.map(t => {
                const taken = takenTimes.has(t);
                return (
                  <button
                    key={t}
                    type="button"
                    disabled={taken}
                    onClick={() => onTimeChange(t)}
                    className={`py-2 rounded-lg text-sm font-medium transition-all border
                      ${taken ? 'border-border text-muted-foreground/50 line-through cursor-not-allowed bg-muted/40'
                        : scheduledTime === t ? 'bg-accent text-accent-foreground border-accent' : 'border-border hover:bg-accent/10 hover:border-accent/30'}
                    `}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const dayLabelsCart = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
function formatCartDayHours(h: any): string {
  if (h && Array.isArray(h.ranges) && h.ranges.length > 0) {
    return h.ranges.map((r: any) => `${r.start} - ${r.end}`).join(", ");
  }
  if (h && h.open && h.close) return `${h.open} - ${h.close}`;
  return "";
}

// Import DINÂMICO: a lib (~3 kB gzip) só entra na rede de quem desbloqueia o
// frete grátis — não pesa no carregamento da loja.
function fireFreeShippingConfetti() {
  if (typeof window === "undefined") return;
  import("canvas-confetti")
    .then(({ default: confetti }) => {
      const colors = ["#ec4899", "#f43f5e", "#22c55e", "#3b82f6", "#f59e0b", "#a855f7"];
      // startVelocity alto com gravity baixa faz o estouro SUBIR antes de cair;
      // origin.y > 1 nasce logo abaixo da borda de baixo da tela.
      const base = {
        colors,
        startVelocity: 55,
        gravity: 0.9,
        ticks: 220,
        scalar: 0.9,
        zIndex: 9999,
        disableForReducedMotion: true,
      };
      confetti({ ...base, particleCount: 60, spread: 55, angle: 60, origin: { x: 0.08, y: 1.05 } });
      confetti({ ...base, particleCount: 60, spread: 55, angle: 120, origin: { x: 0.92, y: 1.05 } });
      setTimeout(() => {
        confetti({ ...base, particleCount: 50, spread: 90, angle: 90, origin: { x: 0.5, y: 1.05 } });
      }, 180);
    })
    .catch(() => {
      // Confete é enfeite: falhar em baixar a lib não pode atrapalhar o pedido.
    });
}

function CartDrawer({ isOpen, onClose, slug, store, cartState, catalogIsOpen, lojaDesativada, selectedUnitId, products, hasFoodProducts, onOpenProduct, onEditItem, pontos, mesa }: { isOpen: boolean, onClose: ()=>void, slug: string, store: CatalogResponse["store"], cartState: ReturnType<typeof createCartStore>, catalogIsOpen: boolean, lojaDesativada?: boolean, selectedUnitId: string | null, products: Product[], hasFoodProducts: boolean, onOpenProduct: (p: Product) => void, onEditItem: (item: CartItem) => void, pontos?: StoreLoyalty, mesa?: string | null }) {
  const [, setLocation] = useLocation();
  const [closedWarningOpen, setClosedWarningOpen] = useState(false);

  // Na mesa o pedido segue pra tela da mesa: sem entrega, sem pagamento agora.
  const checkoutHref = mesa
    ? `/loja/${slug}/mesa`
    : `/loja/${slug}/checkout${selectedUnitId ? `?unitId=${selectedUnitId}` : ''}`;
  // Loja fechada + toggle "receber pedidos com a loja fechada" desligado →
  // o diálogo de fechada vira bloqueio (sem prosseguir pro checkout).
  const blocksWhenClosed = (store as { acceptOrdersWhenClosed?: boolean })?.acceptOrdersWhenClosed === false;
  const proceedToCheckout = () => {
    // replace: substitui a entrada ?cart=open do carrinho pelo checkout, pra que
    // voltar do checkout caia no carrinho (via sentinela do Checkout) e o voltar
    // seguinte caia na loja — sem entradas duplicadas.
    setClosedWarningOpen(false);
    setLocation(checkoutHref, { replace: true });
  };

  const activeDaysCart = store?.businessHours
    ? Object.entries(store.businessHours)
        .filter(([, h]: any) => h?.enabled)
        .sort(([a], [b]) => ((Number(a) + 6) % 7) - ((Number(b) + 6) % 7))
        .map(([key, h]) => ({
          label: dayLabelsCart[Number(key)] || key,
          hours: formatCartDayHours(h),
        }))
        .filter(d => d.hours !== "")
    : [];
  const items = cartState((state) => state.items);
  const total = cartState((state) => state.getTotal());
  const updateQty = cartState((state) => state.updateQuantity);
  const removeItem = cartState((state) => state.removeItem);
  const updateNotes = cartState((state) => state.updateNotes);
  const [notesEditingId, setNotesEditingId] = useState<string | null>(null);
  // Desconto da promo por quantidade POR LINHA (alinhado a items): permite
  // riscar o preço na linha dona do desconto (pedido do Pedro, 09/08). O
  // desconto de uma linha depende das outras do mesmo produto (rank global).
  const lineQtyDiscounts = quantityPromoLineDiscounts(items, "link");
  // Dica dinâmica: SEGUE o desconto (pedido do Pedro, 09/08) — aparece em
  // toda linha do produto que está COM desconto aplicado. Se nenhuma linha
  // tem ainda (abaixo do gatilho), fica na última linha do produto como
  // chamada ("falta 1 para começar").
  const qtyPromoHints = (() => {
    const totals = new Map<string, number>();
    for (const it of items) {
      totals.set(it.product.id, (totals.get(it.product.id) ?? 0) + it.quantity);
    }
    // Contagem POR LINHA (unidades descontadas naquela linha) — a dica fala do
    // que o card carrega, não do produto inteiro.
    const unitDiscs = quantityPromoUnitDiscountsByLine(items, "link");
    const show = new Map<string, string>(); // cartItemId → dica
    const covered = new Set<string>();
    items.forEach((it, idx) => {
      const cfg = getActiveQuantityPromo(it.product, new Date(), "link");
      if (!cfg) return;
      const lineCount = (unitDiscs[idx] || []).filter((d) => d > 0).length;
      const hint = quantityPromoLineHint(cfg, totals.get(it.product.id) ?? 0, lineCount);
      if (hint) {
        show.set(it.cartItemId, hint);
        covered.add(it.product.id);
      }
    });
    // Nenhuma linha descontada ainda (abaixo do gatilho): chamada na última
    // linha do produto ("falta 1 para começar…").
    const lastLineOf = new Map<string, string>();
    for (const it of items) lastLineOf.set(it.product.id, it.cartItemId);
    for (const [productId, qty] of totals) {
      if (covered.has(productId)) continue;
      const item = items.find((i) => i.product.id === productId)!;
      const cfg = getActiveQuantityPromo(item.product, new Date(), "link");
      const hint = quantityPromoHint(cfg, qty);
      if (hint) show.set(lastLineOf.get(productId)!, hint);
    }
    return show;
  })();

  const freeShipThreshold = store?.freeDeliveryAbove ? parseFloat(store.freeDeliveryAbove) : 0;
  const freeShipReached = freeShipThreshold > 0 && total >= freeShipThreshold;
  const prevFreeShipReachedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) return;
    if (freeShipReached && !prevFreeShipReachedRef.current) {
      fireFreeShippingConfetti();
    }
    prevFreeShipReachedRef.current = freeShipReached;
  }, [freeShipReached, isOpen, prevFreeShipReachedRef]);
  const addItem = cartState((state) => state.addItem);

  const suggestiveProducts = useMemo(() => {
    const cartProductIds = new Set(items.map(i => i.product.id));
    return products.filter((p: any) =>
      p.isSuggestive &&
      !cartProductIds.has(p.id) &&
      p.status === 'ativo' &&
      (p.stock === null || p.stock > 0)
    );
  }, [products, items]);

  const suggestiveText = store?.suggestiveText || "Que tal adicionar também?";

  const dataUnicaCarrinho = (store as { orderSingleDate?: boolean }).orderSingleDate === true;
  const handleSuggestiveAdd = (product: Product) => {
    if (productHasOptions(product) || ((product as any).isOrderBased && !dataUnicaCarrinho) || (product as any).soldByWeight) {
      // Abre o modal de produto POR CIMA do carrinho (não fecha o carrinho): o
      // voltar fecha o modal e mantém o carrinho aberto.
      onOpenProduct(product);
    } else {
      addItem(product, 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      {/* Sem backdrop-blur aqui: backdrop-filter em overlay de tela cheia (por
          cima do header sticky que TAMBÉM tem backdrop-blur) corrompe a
          renderização em GPUs Android com driver bugado — faixas de lixo no
          drawer (report 03/07/2026, nobreacai-jp). O preto 60% já cobre o efeito. */}
      <div className="absolute inset-0 bg-black/60 animate-in fade-in duration-300" onClick={onClose} />
      {/* Sobe de baixo e cresce com o conteúdo: SEM `h-full`. A altura é a do
          conteúdo até o teto de 85svh, e aí a lista rola por dentro. `min-h-0`
          na lista é o que permite ela encolher dentro do flex — sem isso o
          conteúdo empurra a folha para além do teto e o rodapé sai da tela. */}
      <div className="relative w-full sm:max-w-md bg-gray-100 max-h-[85svh] shadow-2xl flex flex-col animate-in slide-in-from-bottom rounded-t-3xl duration-300 overflow-hidden">

        <div className="shrink-0 bg-card">
          <div className="mx-auto mt-2.5 h-1.5 w-10 rounded-full bg-muted-foreground/25" />
          <div className="flex items-center justify-between p-4 border-b border-border/50">
            <h2 className="text-xl font-bold flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-secondary"/> Seu Pedido</h2>
            <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full bg-muted/50"><X className="w-5 h-5"/></Button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-6">
          {items.length === 0 ? (
            <div className="min-h-[38svh] flex flex-col items-center justify-center text-muted-foreground space-y-4">
              <img src={`${import.meta.env.BASE_URL}images/empty-cart.png`} className="w-48 h-48 opacity-80 mix-blend-multiply" />
              <p className="font-medium text-lg">Sua sacola está vazia</p>
              <Button variant="outline" onClick={onClose} className="rounded-xl">Adicionar itens</Button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item, itemIdx) => {
                const itemUnit = item.unitPrice !== undefined ? item.unitPrice : Number(item.product.price);
                const grossLine = computeItemTotal({
                  unitPrice: itemUnit,
                  optionsTotal: item.optionsTotal || 0,
                  quantity: item.quantity,
                });
                const qtyDisc = lineQtyDiscounts[itemIdx] || 0;
                const itemTotal = Math.round((grossLine - qtyDisc) * 100) / 100;
                const hasDiscount = (item.discountAmount || 0) > 0 || qtyDisc > 0;
                return (
                  <div key={item.cartItemId} className="flex gap-4 p-3 bg-card shadow rounded-2xl border border-border/50">
                    {(() => {
                      const img = imagemPrincipal(item.product.imageUrl, videoDoCatalogo((item.product as any).video));
                      return img ? <img src={img} className="w-16 h-16 rounded-xl object-cover shrink-0" /> : null;
                    })()}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <p className="font-bold text-foreground leading-tight pr-2">{item.product.name}{item.weightKg != null ? <span className="font-medium text-muted-foreground"> · {pesoLabel(item.weightKg, (item.product as any).weightUnit === "ml" ? "ml" : null)}</span> : null}</p>
                        <div className="flex items-center gap-3 shrink-0">
                          {(productHasOptions(item.product) || ((item.product as any).isOrderBased && !dataUnicaCarrinho)) && (
                            <button onClick={() => onEditItem(item)} className="text-muted-foreground hover:text-primary p-1"><Pencil className="w-3.5 h-3.5"/></button>
                          )}
                          <button onClick={() => removeItem(item.cartItemId)} className="text-muted-foreground hover:text-destructive p-1"><X className="w-4 h-4"/></button>
                        </div>
                      </div>
                      {(() => {
                        // Entrou no carrinho dentro do horário de venda e a faixa fechou: o servidor recusa o pedido; avisa antes.
                        const fora = foraDoHorarioDeVenda(products.find((x) => x.id === item.product.id) ?? item.product);
                        return fora ? (
                          <p className="mt-1 text-xs font-medium text-destructive" data-testid={`carrinho-fora-do-horario-${item.product.id}`}>
                            Fora do horário de venda. {fora}.
                          </p>
                        ) : null;
                      })()}
                      {(() => {
                        // Pacote de escolhas: a conta do item, com o valor dos extras (como na mensagem do pedido).
                        const prep = prepCountFor((item.product as any).optionGroups, (item.product as any).prepCount, item.selectedOptions);
                        if (!prep) return null;
                        const qtdItem = Math.max(1, item.quantity || 1);
                        // «Cada lista conta separado» (Pedro, 28/10/2026: «Como vai saber qual vai em qual copo?»): cada
                        // adicional embaixo da sua lista, com a conta e o valor dos extras dela no título.
                        if (prep.listas) {
                          return (
                            <div className="mt-1 space-y-1">
                              {gruposComConta(item.selectedOptions, prep, qtdItem).map((g, gi) => (
                                <div key={gi}>
                                  {g.titulo && (
                                    <p className="text-xs font-medium text-foreground" data-testid={g.conta ? "cart-prep-line" : undefined}>
                                      {g.titulo}{g.conta ? `: ${g.conta}` : ""}{g.extrasValue > 0 ? ` ${formatCurrency(g.extrasValue * qtdItem)}` : ""}
                                    </p>
                                  )}
                                  {g.opcoes.map((o, oi) => (
                                    <p key={oi} className="text-xs text-muted-foreground">
                                      + {o.count > 1 ? `${o.count}x ` : ""}{o.nome}{o.preco > 0 ? ` ${formatCurrency(o.preco * o.count)}` : ""}
                                    </p>
                                  ))}
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return (
                          <p className="text-xs font-medium text-foreground mt-1" data-testid="cart-prep-line">
                            {prepCountLine(prep)}{prep.extrasValue > 0 ? ` ${formatCurrency(prep.extrasValue * qtdItem)}` : ""}
                          </p>
                        );
                      })()}
                      {item.selectedOptions && item.selectedOptions.length > 0 && !prepCountFor((item.product as any).optionGroups, (item.product as any).prepCount, item.selectedOptions)?.listas && (
                        <div className="mt-1 space-y-0.5">
                          {/* count x quantidade do item: o adicional é por unidade,
                              então 2 lanches com 1 carne extra cada são 2 carnes.
                              Mostrar "1x" aqui fez cliente e lojista discordarem
                              do que tinha sido pedido (Terraço 29, 03/08/2026). */}
                          {Object.values(item.selectedOptions.reduce<Record<string, { opt: SelectedOption; count: number }>>((acc, opt) => {
                            const key = `${opt.groupName}:${opt.optionName}`;
                            if (!acc[key]) acc[key] = { opt, count: 0 };
                            acc[key].count += Math.max(1, item.quantity || 1);
                            return acc;
                          }, {})).map(({ opt, count }, i) => (
                            <p key={i} className="text-xs text-muted-foreground">
                              + {count > 1 ? `${count}x ` : ''}{opt.optionName}{opt.price > 0 ? ` ${formatCurrency(opt.price * count)}` : ''}
                            </p>
                          ))}
                        </div>
                      )}
                      {item.removedIngredients && item.removedIngredients.length > 0 && (
                        <p className="text-xs text-destructive/70 mt-0.5">Retirar: {item.removedIngredients.join(', ')}</p>
                      )}
                      {item.scheduledDate && (
                        <p className="text-xs text-primary mt-1 flex items-center gap-1">
                          <CalendarDays className="w-3 h-3" />
                          {encomendaPorExtenso(item.scheduledDate, item.scheduledTime, item.scheduledShift)}
                        </p>
                      )}
                      {item.notes && notesEditingId !== item.cartItemId && (
                        <p className="text-xs text-muted-foreground mt-1 italic">"{item.notes}"</p>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex flex-col gap-2 shrink-0">
                          <div className="flex items-center self-start bg-background border border-border/50 rounded-lg">
                            <button onClick={() => updateQty(item.cartItemId, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-primary"><Minus className="w-3 h-3"/></button>
                            <span className="w-8 text-center text-sm font-bold">{item.quantity}</span>
                            <button onClick={() => updateQty(item.cartItemId, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-primary"><Plus className="w-3 h-3"/></button>
                          </div>
                          <button
                            type="button"
                            onClick={() => setNotesEditingId(notesEditingId === item.cartItemId ? null : item.cartItemId)}
                            className="flex items-center gap-1 text-[11px] font-medium text-primary hover:bg-primary/10 px-2 py-1 rounded-md transition-colors"
                          >
                            <Pencil className="w-3 h-3" />
                            {item.notes ? "Editar observação" : "Adicionar observação"}
                          </button>
                        </div>
                        <div className="text-right">
                          {hasDiscount && (
                            <p className="text-xs text-muted-foreground line-through leading-none">
                              {formatCurrency(qtyDisc > 0 ? grossLine : computeItemTotal({
                                unitPrice: item.originalUnitPrice ?? itemUnit,
                                optionsTotal: item.optionsTotal || 0,
                                quantity: item.quantity,
                              }))}
                            </p>
                          )}
                          <p className={`font-bold ${hasDiscount ? "text-accent" : "text-primary"}`}>{formatCurrency(itemTotal)}</p>
                        </div>
                      </div>
                      {qtyPromoHints.has(item.cartItemId) && (
                        <p className="mt-2 text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg px-2 py-1">
                          {qtyPromoHints.get(item.cartItemId)}
                        </p>
                      )}
                      {notesEditingId === item.cartItemId && (
                        <div className="mt-3 space-y-2">
                          <textarea
                            autoFocus
                            placeholder={hasFoodProducts ? "Ex: tirar cebola, ponto da carne..." : "Ex: embalagem para presente, cor preferida..."}
                            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary outline-none resize-none min-h-[64px]"
                            value={item.notes ?? ""}
                            onChange={(e) => updateNotes(item.cartItemId, e.target.value)}
                          />
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => setNotesEditingId(null)}
                              className="text-[11px] font-semibold text-primary px-2 py-1 rounded-md hover:bg-primary/10"
                            >
                              Pronto
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {items.length > 0 && suggestiveProducts.length > 0 && (
            <div className="-mx-1 pt-2">
              <div className="flex items-center gap-2 mb-3 px-1">
                <Sparkles className="w-4 h-4 text-secondary" />
                <p className="text-sm font-semibold text-foreground">{suggestiveText}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 px-1">
                {suggestiveProducts.map((sp: any) => {
                  // Preço nas opções (base 0): «A partir de», como no card do cardápio (Pedro, 23/10/2026, DoceAmor: «Ele tem
                  // que aparecer do mesmo jeito que aparece no produto»). Antes saía R$ 0,00. Com a promoção em % nas
                  // opções, o valor com desconto e o original riscado.
                  const sAPartirDe = pricesByOptions(sp) ? aPartirDeComPromocao(sp, "link") : null;
                  const sPromo = getActivePromotion(sp, new Date(), "link");
                  return (
                    <div key={sp.id} className="bg-card shadow border border-border/50 rounded-xl p-2 flex flex-col">
                      {(() => {
                        const img = imagemPrincipal(sp.imageUrl, videoDoCatalogo((sp as any).video));
                        return img ? <img src={img} className="w-full h-16 rounded-lg object-cover mb-1.5" loading="lazy" /> : null;
                      })()}
                      <p className="text-xs font-bold leading-tight line-clamp-2">{sp.name}</p>
                      {sAPartirDe && sAPartirDe.original != null ? (
                        <div className="flex items-baseline gap-1 mt-1 flex-wrap" data-testid={`suggestive-price-${sp.id}`}>
                          <span className="text-xs font-medium text-muted-foreground">A partir de</span>
                          <span className="text-xs font-bold text-accent">{formatCurrency(sAPartirDe.valor)}</span>
                          <span className="text-[10px] text-muted-foreground line-through">{formatCurrency(sAPartirDe.original)}</span>
                        </div>
                      ) : sAPartirDe ? (
                        <p className="text-xs font-bold text-primary mt-1" data-testid={`suggestive-price-${sp.id}`}>
                          <span className="font-medium text-muted-foreground">A partir de </span>{formatCurrency(sAPartirDe.valor)}
                        </p>
                      ) : sPromo.active ? (
                        <div className="flex items-baseline gap-1 mt-1 flex-wrap">
                          <span className="text-xs font-bold text-accent">{formatCurrency(sPromo.finalPrice)}</span>
                          <span className="text-[10px] text-muted-foreground line-through">{formatCurrency(sPromo.originalPrice)}</span>
                        </div>
                      ) : (
                        <p className="text-xs font-bold text-primary mt-1">{formatCurrency(sPromo.originalPrice)}</p>
                      )}
                      <button
                        onClick={() => handleSuggestiveAdd(sp)}
                        className="mt-2 text-xs bg-primary text-primary-foreground rounded-lg py-1.5 font-semibold hover:bg-primary/90 transition-colors"
                      >
                        Adicionar
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="shrink-0 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-border/50 bg-card shadow-[0_-10px_40px_rgba(0,0,0,0.05)]">
            {(() => {
              const threshold = store.freeDeliveryAbove && !mesa ? parseFloat(store.freeDeliveryAbove) : 0;
              const belowMin = !mesa && !!(store.minOrderValue && total < store.minOrderValue);
              const feeOnRequest = (store as any)?.deliveryFeeType === "on_request";
              const freteNode = threshold > 0 && !feeOnRequest ? (() => {
                const reached = total >= threshold;
                const progress = Math.min((total / threshold) * 100, 100);
                const remaining = threshold - total;
                return (
                  <div className={`text-[11px] leading-tight mb-2 ${reached ? 'text-green-700' : 'text-blue-700'}`}>
                    <p className="font-medium">
                      {reached ? 'Frete grátis desbloqueado!' : `Faltam ${formatCurrency(remaining)} para frete grátis`}
                    </p>
                    <div className="mt-1 w-full h-1 bg-muted rounded-full overflow-hidden">
                      {/* `hsl(var(--primary))`, não `var(--primary)`: a variável
                          guarda só os componentes ("20 90% 55%"), então sem o
                          hsl() a declaração é INVÁLIDA e o browser descarta —
                          a barra ficava sem cor nenhuma até virar verde. A cor
                          é a da loja: useStoreColors escreve --primary no
                          documentElement a partir de store.colorPrimary. */}
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: reached ? '#22c55e' : 'hsl(var(--primary))' }} />
                    </div>
                  </div>
                );
              })() : null;
              const minNode = belowMin ? (
                <div className="text-[11px] leading-tight text-orange-700 flex items-center justify-end gap-1 mt-2 mb-2">
                  <Info className="w-3 h-3 shrink-0" />
                  <span>Faltam {formatCurrency(store.minOrderValue! - total)} para o pedido mínimo de {formatCurrency(store.minOrderValue!)}</span>
                </div>
              ) : null;
              // Promo por quantidade: o desconto aparece RISCADO na própria
              // linha (preferência do Pedro, 09/08) — as linhas exibidas já
              // somam o Total, então não há linha agregada aqui.
              // Pontos: quanto este carrinho rende, (com o número confirmado) quanto falta para o próximo prêmio e, com
              // "1 ponto a cada R$ 15" ou pedido mínimo, quanto falta em reais para render mais (Pedro, 06/10/2026).
              const prog = pontos?.enabled ? pontos.program : undefined;
              const cfgPontos = prog ? normalizeLoyaltyConfig(prog) : null;
              const diaPontos = diaDaSemanaNaLoja(getStoreTimezone());
              const rende = cfgPontos ? pointsForOrder(cfgPontos, { base: total, weekday: diaPontos }) : 0;
              const degrau = cfgPontos ? nextLoyaltyStep(cfgPontos, { base: total, weekday: diaPontos }) : null;
              const proximo = prog && pontos?.verified ? proximoPremio(prog.rewards, (pontos.balance ?? 0) + rende) : null;
              const pontosNode = rende > 0 || degrau ? (
                <div className="text-[11px] leading-tight text-primary flex items-start gap-1 mb-2" data-testid="carrinho-pontos">
                  <Gift className="w-3 h-3 shrink-0" />
                  <span>
                    {rende > 0 && (
                      <>
                        +{fmtPts(rende)} com este pedido
                        {proximo ? ` · faltam ${fmtPts(proximo.faltam)} para ${proximo.reward.name}` : ""}
                      </>
                    )}
                    {degrau && (
                      <span className="block" data-testid="carrinho-pontos-faltam">
                        Faltam {formatCurrency(degrau.faltam)} para {rende > 0 ? "mais" : "ganhar"} {fmtPontos(degrau.ganha)}
                      </span>
                    )}
                  </span>
                </div>
              ) : null;
              return (
                <>
                  {freteNode}
                  {pontosNode}
                  {(store as { orderSingleDate?: boolean }).orderSingleDate === true && items.some((it) => (it.product as any)?.isOrderBased) && (
                    <div className="text-[11px] leading-tight text-primary flex items-center gap-1 mb-2" data-testid="carrinho-data-no-proximo-passo">
                      <CalendarDays className="w-3 h-3 shrink-0" />
                      <span>Data e horário da encomenda: no próximo passo.</span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline gap-3 mb-2">
                    <span className="font-medium text-muted-foreground text-lg">Total</span>
                    <span className="font-bold text-2xl shrink-0">{formatCurrency(total)}</span>
                  </div>
                  {minNode}
                </>
              );
            })()}

            {lojaDesativada ? (
              // Sem rota pro checkout: o pedido seria recusado pelo servidor.
              <>
                <StoreInactiveCard className="mb-3" />
                <Button className="w-full h-14 rounded-xl text-lg font-bold" disabled>Loja não está recebendo pedidos</Button>
              </>
            ) : !mesa && store.minOrderValue && total < store.minOrderValue ? (
              <Button className="w-full h-14 rounded-xl text-lg font-bold" disabled>Continuar</Button>
            ) : catalogIsOpen ? (
              <Button asChild className="w-full h-14 rounded-xl text-lg font-bold shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all hover:-translate-y-0.5">
                <Link href={checkoutHref} replace>Continuar</Link>
              </Button>
            ) : (
              <Button
                className="w-full h-14 rounded-xl text-lg font-bold shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all hover:-translate-y-0.5"
                onClick={() => setClosedWarningOpen(true)}
              >
                Continuar
              </Button>
            )}
          </div>
        )}
      </div>

      <Dialog open={closedWarningOpen} onOpenChange={setClosedWarningOpen}>
        <DialogContent className="max-w-sm">
          <div className="space-y-4">
            <div className="flex items-start gap-2">
              <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <h3 className="font-semibold text-base">Loja fechada no momento</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {blocksWhenClosed
                    ? "Esta loja não aceita pedidos fora do horário de funcionamento. Volte num dos horários abaixo — seu carrinho fica salvo."
                    : "Você pode prosseguir normalmente com seu pedido. Daremos andamento no próximo horário disponível."}
                </p>
              </div>
            </div>
            {activeDaysCart.length > 0 && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800 p-3">
                <p className="text-xs font-semibold text-orange-800 dark:text-orange-300 mb-2">Horários de funcionamento</p>
                <div className="space-y-0.5">
                  {activeDaysCart.map((d) => (
                    <p key={d.label} className="text-xs text-orange-700 dark:text-orange-400">
                      <span className="font-medium">{d.label}:</span> {d.hours}
                    </p>
                  ))}
                </div>
              </div>
            )}
            {blocksWhenClosed ? (
              <Button className="w-full h-11 rounded-xl font-semibold" variant="outline" onClick={() => setClosedWarningOpen(false)}>
                Entendi
              </Button>
            ) : (
              <Button className="w-full h-11 rounded-xl font-semibold" onClick={proceedToCheckout}>
                Entendi
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CatalogSkeleton() {
  return (
    <div className="min-h-[100svh] bg-gray-100 max-w-3xl mx-auto">
      <div className="h-16 bg-white border-b flex items-center px-4 gap-3"><Skeleton className="w-10 h-10 rounded-full"/><Skeleton className="w-32 h-5"/></div>
      <div className="p-4"><Skeleton className="w-full h-12 rounded-xl"/></div>
      <div className="p-4 space-y-4">{[1,2,3].map(i=><Skeleton key={i} className="w-full h-32 rounded-2xl"/>)}</div>
    </div>
  );
}
